import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import enLocale from './i18n/locales/en.json';
import frLocale from './i18n/locales/fr.json';
import esLocale from './i18n/locales/es.json';

// English translations
const enTranslations = {
  common: {
    appName: 'Tickally',
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    submit: 'Submit',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    welcome: 'Welcome',
    logout: 'Logout',
    profile: 'Profile',
    settings: 'Settings',
    dashboard: 'Dashboard',
    members: 'Members',
    attendance: 'Attendance',
    dues: 'Dues',
    donations: 'Donations',
    finances: 'Finances',
    subscription: 'Subscription',
    reports: 'Reports',
    sidebar: {
      switchOrganization: 'Switch Organization',
    },
    navigation: {
      dashboard: 'Dashboard',
      members: 'Members',
      attendance: 'Attendance',
      dues: 'Dues',
      donations: 'Donations',
      finances: 'Finances',
      subscription: 'Subscription',
      settings: 'Settings',
      myRecord: 'My Record',
      profile: 'Profile',
    },
    total: 'Total',
    amount: 'Amount',
    date: 'Date',
    name: 'Name',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    actions: 'Actions',
    view: 'View',
    add: 'Add',
    remove: 'Remove',
    update: 'Update',
    language: 'Language',
    currency: 'Currency',
    app: {
      name: 'Tickally',
      tagline: 'Organization Management',
    },
    organization: {
      title: 'Organization',
      select: 'Select Organization',
      create: 'Create Organization',
    },
    role: 'Role',
    admin: 'Admin',
    treasurer: 'Treasurer',
    secretary: 'Secretary',
    member: 'Member',
    all: 'All',
    none: 'None',
    select: 'Select',
    selected: 'Selected',
    noData: 'No data available',
    noResults: 'No results found',
    tryAgain: 'Try again',
    refresh: 'Refresh',
    more: 'More',
    less: 'Less',
    show: 'Show',
    hide: 'Hide',
    from: 'From',
    to: 'To',
    of: 'of',
    items: 'items',
    perPage: 'per page',
    goToPage: 'Go to page',
    page: 'Page',
    pages: 'Pages',
  },
  auth: {
    login: 'Login',
    register: 'Register',
    fullName: 'Full Name',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    forgotPassword: 'Forgot Password?',
    resetPassword: 'Reset Password',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    hasAccount: 'Already have an account?',
    signUp: 'Sign Up',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    createAccount: 'Create Account',
    loginSuccess: 'Login successful',
    loginError: 'Login failed',
    registerSuccess: 'Registration successful',
    registerError: 'Registration failed',
    passwordMismatch: 'Passwords do not match',
    invalidEmail: 'Invalid email address',
    weakPassword: 'Password is too weak',
    requiredField: 'This field is required',
    firstName: 'First Name',
    lastName: 'Last Name',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back',
    overview: 'Overview',
    quickStats: 'Quick Stats',
    recentActivity: 'Recent Activity',
    totalMembers: 'Total Members',
    activeMembers: 'Active Members',
    attendanceToday: 'Attendance Today',
    duesCollected: 'Dues Collected',
    pendingDues: 'Pending Dues',
    totalDonations: 'Total Donations',
    upcomingEvents: 'Upcoming Events',
    recentPayments: 'Recent Payments',
    viewAll: 'View All',
    seeMore: 'See More',
  },
  members: {
    title: 'Members',
    addMember: 'Add Member',
    editMember: 'Edit Member',
    deleteMember: 'Delete Member',
    memberDetails: 'Member Details',
    memberList: 'Member List',
    memberCard: 'Member Card',
    firstName: 'First Name',
    lastName: 'Last Name',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone Number',
    address: 'Address',
    joinDate: 'Join Date',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    searchMembers: 'Search members...',
    filterByStatus: 'Filter by status',
    noMembers: 'No members found',
    memberSince: 'Member since',
    lastAttendance: 'Last attendance',
    totalAttendance: 'Total attendance',
    duesPaid: 'Dues paid',
    duesOwed: 'Dues owed',
    confirmDelete: 'Are you sure you want to delete this member?',
    deleteSuccess: 'Member deleted successfully',
    deleteError: 'Failed to delete member',
    saveSuccess: 'Member saved successfully',
    saveError: 'Failed to save member',
  },
  attendance: {
    title: 'Attendance',
    takeAttendance: 'Take Attendance',
    meetingList: 'Meeting List',
    addMeeting: 'Add Meeting',
    editMeeting: 'Edit Meeting',
    meetingName: 'Meeting Name',
    meetingDate: 'Meeting Date',
    meetingType: 'Meeting Type',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    excused: 'Excused',
    attendanceRate: 'Attendance Rate',
    totalPresent: 'Total Present',
    totalAbsent: 'Total Absent',
    markAllPresent: 'Mark All Present',
    markAllAbsent: 'Mark All Absent',
    saveAttendance: 'Save Attendance',
    attendanceSaved: 'Attendance saved successfully',
    attendanceError: 'Failed to save attendance',
    noMeetings: 'No meetings found',
    searchMeetings: 'Search meetings...',
  },
  dues: {
    title: 'Dues',
    collectDues: 'Collect Dues',
    duesCollection: 'Dues Collection',
    monthlyDues: 'Monthly Dues',
    duesAmount: 'Dues Amount',
    amountPaid: 'Amount Paid',
    amountOwed: 'Amount Owed',
    paymentDate: 'Payment Date',
    paymentMethod: 'Payment Method',
    cash: 'Cash',
    bankTransfer: 'Bank Transfer',
    mobileMoney: 'Mobile Money',
    check: 'Check',
    other: 'Other',
    paymentFor: 'Payment For',
    currentMonth: 'Current Month',
    previousMonths: 'Previous Months',
    advancePayment: 'Advance Payment',
    backlog: 'Backlog',
    prepay: 'Prepay',
    allocatePayment: 'Allocate Payment',
    allocationSuccess: 'Payment allocated successfully',
    allocationError: 'Failed to allocate payment',
    noDues: 'No dues records found',
    memberDues: 'Member Dues',
    allMembersDues: 'All Members Dues',
    paymentHistory: 'Payment History',
    totalCollected: 'Total Collected',
    totalPending: 'Total Pending',
    selectMember: 'Select Member',
    enterAmount: 'Enter Amount',
  },
  donations: {
    title: 'Donations',
    donationEvents: 'Donation Events',
    addEvent: 'Add Event',
    editEvent: 'Edit Event',
    eventName: 'Event Name',
    eventDate: 'Event Date',
    eventDescription: 'Description',
    targetAmount: 'Target Amount',
    collectedAmount: 'Collected Amount',
    remainingAmount: 'Remaining Amount',
    donorName: 'Donor Name',
    donorEmail: 'Donor Email',
    donorPhone: 'Donor Phone',
    donationAmount: 'Donation Amount',
    addDonation: 'Add Donation',
    recordDonation: 'Record Donation',
    anonymous: 'Anonymous',
    eventStatus: 'Event Status',
    ongoing: 'Ongoing',
    completed: 'Completed',
    cancelled: 'Cancelled',
    noEvents: 'No donation events found',
    noDonations: 'No donations recorded',
    deleteEventConfirm: 'Are you sure you want to delete this event?',
    eventDeleted: 'Event deleted successfully',
    donationRecorded: 'Donation recorded successfully',
  },
  finances: {
    title: 'Finances',
    financialSummary: 'Financial Summary',
    income: 'Income',
    expenses: 'Expenses',
    netIncome: 'Net Income',
    balance: 'Balance',
    totalIncome: 'Total Income',
    totalExpenses: 'Total Expenses',
    addExpense: 'Add Expense',
    expenseCategory: 'Category',
    expenseDescription: 'Description',
    expenseAmount: 'Amount',
    expenseDate: 'Date',
    categories: {
      rent: 'Rent',
      utilities: 'Utilities',
      supplies: 'Supplies',
      maintenance: 'Maintenance',
      events: 'Events',
      salaries: 'Salaries',
      marketing: 'Marketing',
      other: 'Other',
    },
    period: 'Period',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    thisQuarter: 'This Quarter',
    thisYear: 'This Year',
    custom: 'Custom',
    generateReport: 'Generate Report',
    exportReport: 'Export Report',
    noTransactions: 'No transactions found',
  },
  settings: {
    title: 'Settings',
    organizationSettings: 'Organization Settings',
    organizationName: 'Organization Name',
    organizationDescription: 'Description',
    organizationEmail: 'Organization Email',
    organizationPhone: 'Organization Phone',
    organizationAddress: 'Organization Address',
    defaultCurrency: 'Default Currency',
    defaultLanguage: 'Default Language',
    monthlyDuesAmount: 'Monthly Dues Amount',
    logo: 'Organization Logo',
    uploadLogo: 'Upload Logo',
    removeLogo: 'Remove Logo',
    userManagement: 'User Management',
    inviteUser: 'Invite User',
    removeUser: 'Remove User',
    changeRole: 'Change Role',
    appSettings: 'App Settings',
    notifications: 'Notifications',
    emailNotifications: 'Email Notifications',
    pushNotifications: 'Push Notifications',
    darkMode: 'Dark Mode',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved successfully',
    settingsError: 'Failed to save settings',
  },
  memberPortal: {
    title: 'My Record',
    myAttendance: 'My Attendance',
    myDues: 'My Dues',
    myDonations: 'My Donations',
    personalInfo: 'Personal Information',
    attendanceHistory: 'Attendance History',
    duesHistory: 'Dues History',
    paymentStatus: 'Payment Status',
    upToDate: 'Up to Date',
    overdue: 'Overdue',
    partial: 'Partial',
    totalOwed: 'Total Owed',
    totalPaid: 'Total Paid',
    nextPayment: 'Next Payment',
    memberSince: 'Member Since',
  },
  errors: {
    general: 'Something went wrong',
    network: 'Network error. Please check your connection',
    unauthorized: 'Unauthorized access',
    forbidden: 'Access forbidden',
    notFound: 'Page not found',
    serverError: 'Server error',
    tryAgain: 'Please try again later',
    sessionExpired: 'Session expired. Please login again',
  },
  offline: {
    offline: 'You are offline',
    online: 'You are back online',
    syncPending: 'Changes will sync when online',
    syncing: 'Syncing...',
    synced: 'All changes synced',
  },
};

// French translations
const frTranslations = {
  common: {
    appName: 'Tickally',
    loading: 'Chargement...',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    search: 'Rechercher',
    filter: 'Filtrer',
    close: 'Fermer',
    confirm: 'Confirmer',
    back: 'Retour',
    next: 'Suivant',
    previous: 'Précédent',
    submit: 'Soumettre',
    success: 'Succès',
    error: 'Erreur',
    warning: 'Avertissement',
    info: 'Info',
    yes: 'Oui',
    no: 'Non',
    ok: 'OK',
    welcome: 'Bienvenue',
    logout: 'Déconnexion',
    profile: 'Profil',
    settings: 'Paramètres',
    dashboard: 'Tableau de Bord',
    members: 'Membres',
    attendance: 'Présence',
    dues: 'Cotisations',
    donations: 'Dons',
    finances: 'Finances',
    reports: 'Rapports',
    total: 'Total',
    amount: 'Montant',
    date: 'Date',
    name: 'Nom',
    fullName: 'Nom Complet',
    email: 'Email',
    phone: 'Téléphone',
    address: 'Adresse',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    pending: 'En attente',
    actions: 'Actions',
    view: 'Voir',
    add: 'Ajouter',
    remove: 'Retirer',
    update: 'Mettre à jour',
    language: 'Langue',
    currency: 'Devise',
    app: {
      name: 'Tickally',
      tagline: 'Gestion des Organisations',
    },
    organization: {
      title: 'Organisation',
      select: 'Sélectionner une Organisation',
      create: 'Créer une Organisation',
    },
    navigation: {
      dashboard: 'Tableau de Bord',
      members: 'Membres',
      attendance: 'Présences',
      dues: 'Cotisations',
      donations: 'Dons',
      finances: 'Finances',
      subscription: 'Abonnement',
      settings: 'Paramètres',
      myRecord: 'Mon Dossier',
      profile: 'Profil',
    },
    sidebar: {
      switchOrganization: 'Changer d\'Organisation',
    },
    role: 'Rôle',
    admin: 'Administrateur',
    treasurer: 'Trésorier',
    secretary: 'Secrétaire',
    member: 'Membre',
    all: 'Tous',
    none: 'Aucun',
    select: 'Sélectionner',
    selected: 'Sélectionné',
    noData: 'Aucune donnée disponible',
    noResults: 'Aucun résultat trouvé',
    tryAgain: 'Réessayer',
    refresh: 'Actualiser',
    more: 'Plus',
    less: 'Moins',
    show: 'Afficher',
    hide: 'Masquer',
    from: 'De',
    to: 'À',
    of: 'de',
    items: 'éléments',
    perPage: 'par page',
    goToPage: 'Aller à la page',
    page: 'Page',
    pages: 'Pages',
  },
  auth: {
    login: 'Connexion',
    register: 'Inscription',
    fullName: 'Nom Complet',
    email: 'Email',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    forgotPassword: 'Mot de passe oublié?',
    resetPassword: 'Réinitialiser le mot de passe',
    dontHaveAccount: "Vous n'avez pas de compte?",
    alreadyHaveAccount: 'Vous avez déjà un compte?',
    hasAccount: 'Vous avez déjà un compte?',
    signUp: "S'inscrire",
    signIn: 'Se connecter',
    signOut: 'Se déconnecter',
    createAccount: 'Créer un compte',
    loginSuccess: 'Connexion réussie',
    loginError: 'Échec de la connexion',
    registerSuccess: 'Inscription réussie',
    registerError: "Échec de l'inscription",
    passwordMismatch: 'Les mots de passe ne correspondent pas',
    invalidEmail: 'Adresse email invalide',
    weakPassword: 'Mot de passe trop faible',
    requiredField: 'Ce champ est obligatoire',
    firstName: 'Prénom',
    lastName: 'Nom',
  },
  dashboard: {
    title: 'Tableau de Bord',
    welcome: 'Bon retour',
    overview: 'Aperçu',
    quickStats: 'Statistiques Rapides',
    recentActivity: 'Activité Récente',
    totalMembers: 'Total des Membres',
    activeMembers: 'Membres Actifs',
    attendanceToday: 'Présence Aujourd\'hui',
    duesCollected: 'Cotisations Collectées',
    pendingDues: 'Cotisations en Attente',
    totalDonations: 'Total des Dons',
    upcomingEvents: 'Événements à Venir',
    recentPayments: 'Paiements Récents',
    viewAll: 'Voir Tout',
    seeMore: 'Voir Plus',
  },
  members: {
    title: 'Membres',
    addMember: 'Ajouter un Membre',
    editMember: 'Modifier le Membre',
    deleteMember: 'Supprimer le Membre',
    memberDetails: 'Détails du Membre',
    memberList: 'Liste des Membres',
    memberCard: 'Carte de Membre',
    firstName: 'Prénom',
    lastName: 'Nom',
    fullName: 'Nom Complet',
    email: 'Email',
    phone: 'Numéro de Téléphone',
    address: 'Adresse',
    joinDate: "Date d'adhésion",
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    searchMembers: 'Rechercher des membres...',
    filterByStatus: 'Filtrer par statut',
    noMembers: 'Aucun membre trouvé',
    memberSince: 'Membre depuis',
    lastAttendance: 'Dernière présence',
    totalAttendance: 'Présence totale',
    duesPaid: 'Cotisations payées',
    duesOwed: 'Cotisations dues',
    confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce membre?',
    deleteSuccess: 'Membre supprimé avec succès',
    deleteError: 'Échec de la suppression du membre',
    saveSuccess: 'Membre enregistré avec succès',
    saveError: "Échec de l'enregistrement du membre",
  },
  attendance: {
    title: 'Présence',
    takeAttendance: 'Prendre la Présence',
    meetingList: 'Liste des Réunions',
    addMeeting: 'Ajouter une Réunion',
    editMeeting: 'Modifier la Réunion',
    meetingName: 'Nom de la Réunion',
    meetingDate: 'Date de la Réunion',
    meetingType: 'Type de Réunion',
    present: 'Présent',
    absent: 'Absent',
    late: 'En retard',
    excused: 'Excusé',
    attendanceRate: 'Taux de Présence',
    totalPresent: 'Total Présent',
    totalAbsent: 'Total Absent',
    markAllPresent: 'Marquer Tous Présents',
    markAllAbsent: 'Marquer Tous Absents',
    saveAttendance: 'Enregistrer la Présence',
    attendanceSaved: 'Présence enregistrée avec succès',
    attendanceError: "Échec de l'enregistrement de la présence",
    noMeetings: 'Aucune réunion trouvée',
    searchMeetings: 'Rechercher des réunions...',
  },
  dues: {
    title: 'Cotisations',
    collectDues: 'Collecter les Cotisations',
    duesCollection: 'Collecte des Cotisations',
    monthlyDues: 'Cotisations Mensuelles',
    duesAmount: 'Montant des Cotisations',
    amountPaid: 'Montant Payé',
    amountOwed: 'Montant Dû',
    paymentDate: 'Date de Paiement',
    paymentMethod: 'Mode de Paiement',
    cash: 'Espèces',
    bankTransfer: 'Virement Bancaire',
    mobileMoney: 'Mobile Money',
    check: 'Chèque',
    other: 'Autre',
    paymentFor: 'Paiement Pour',
    currentMonth: 'Mois en Cours',
    previousMonths: 'Mois Précédents',
    advancePayment: 'Paiement Anticipé',
    backlog: 'Arriérés',
    prepay: 'Prépaiement',
    allocatePayment: 'Allouer le Paiement',
    allocationSuccess: 'Paiement alloué avec succès',
    allocationError: "Échec de l'allocation du paiement",
    noDues: 'Aucun enregistrement de cotisations',
    memberDues: 'Cotisations du Membre',
    allMembersDues: 'Cotisations de Tous les Membres',
    paymentHistory: 'Historique des Paiements',
    totalCollected: 'Total Collecté',
    totalPending: 'Total en Attente',
    selectMember: 'Sélectionner un Membre',
    enterAmount: 'Entrer le Montant',
  },
  donations: {
    title: 'Dons',
    donationEvents: 'Événements de Dons',
    addEvent: 'Ajouter un Événement',
    editEvent: "Modifier l'Événement",
    eventName: "Nom de l'Événement",
    eventDate: "Date de l'Événement",
    eventDescription: 'Description',
    targetAmount: 'Montant Cible',
    collectedAmount: 'Montant Collecté',
    remainingAmount: 'Montant Restant',
    donorName: 'Nom du Donateur',
    donorEmail: 'Email du Donateur',
    donorPhone: 'Téléphone du Donateur',
    donationAmount: 'Montant du Don',
    addDonation: 'Ajouter un Don',
    recordDonation: 'Enregistrer un Don',
    anonymous: 'Anonyme',
    eventStatus: "Statut de l'Événement",
    ongoing: 'En cours',
    completed: 'Terminé',
    cancelled: 'Annulé',
    noEvents: 'Aucun événement de don trouvé',
    noDonations: 'Aucun don enregistré',
    deleteEventConfirm: 'Êtes-vous sûr de vouloir supprimer cet événement?',
    eventDeleted: 'Événement supprimé avec succès',
    donationRecorded: 'Don enregistré avec succès',
  },
  finances: {
    title: 'Finances',
    financialSummary: 'Résumé Financier',
    income: 'Revenus',
    expenses: 'Dépenses',
    netIncome: 'Revenu Net',
    balance: 'Solde',
    totalIncome: 'Revenus Totaux',
    totalExpenses: 'Dépenses Totales',
    addExpense: 'Ajouter une Dépense',
    expenseCategory: 'Catégorie',
    expenseDescription: 'Description',
    expenseAmount: 'Montant',
    expenseDate: 'Date',
    categories: {
      rent: 'Loyer',
      utilities: 'Services Publics',
      supplies: 'Fournitures',
      maintenance: 'Maintenance',
      events: 'Événements',
      salaries: 'Salaires',
      marketing: 'Marketing',
      other: 'Autre',
    },
    period: 'Période',
    thisMonth: 'Ce Mois',
    lastMonth: 'Mois Dernier',
    thisQuarter: 'Ce Trimestre',
    thisYear: 'Cette Année',
    custom: 'Personnalisé',
    generateReport: 'Générer un Rapport',
    exportReport: 'Exporter le Rapport',
    noTransactions: 'Aucune transaction trouvée',
  },
  settings: {
    title: 'Paramètres',
    organizationSettings: "Paramètres de l'Organisation",
    organizationName: "Nom de l'Organisation",
    organizationDescription: 'Description',
    organizationEmail: "Email de l'Organisation",
    organizationPhone: "Téléphone de l'Organisation",
    organizationAddress: "Adresse de l'Organisation",
    defaultCurrency: 'Devise par Défaut',
    defaultLanguage: 'Langue par Défaut',
    monthlyDuesAmount: 'Montant des Cotisations Mensuelles',
    logo: "Logo de l'Organisation",
    uploadLogo: 'Télécharger le Logo',
    removeLogo: 'Supprimer le Logo',
    userManagement: 'Gestion des Utilisateurs',
    inviteUser: 'Inviter un Utilisateur',
    removeUser: 'Retirer un Utilisateur',
    changeRole: 'Changer le Rôle',
    appSettings: "Paramètres de l'Application",
    notifications: 'Notifications',
    emailNotifications: 'Notifications par Email',
    pushNotifications: 'Notifications Push',
    darkMode: 'Mode Sombre',
    saveSettings: 'Enregistrer les Paramètres',
    settingsSaved: 'Paramètres enregistrés avec succès',
    settingsError: "Échec de l'enregistrement des paramètres",
  },
  memberPortal: {
    title: 'Mon Dossier',
    myAttendance: 'Ma Présence',
    myDues: 'Mes Cotisations',
    myDonations: 'Mes Dons',
    personalInfo: 'Informations Personnelles',
    attendanceHistory: 'Historique de Présence',
    duesHistory: 'Historique des Cotisations',
    paymentStatus: 'Statut de Paiement',
    upToDate: 'À Jour',
    overdue: 'En Retard',
    partial: 'Partiel',
    totalOwed: 'Total Dû',
    totalPaid: 'Total Payé',
    nextPayment: 'Prochain Paiement',
    memberSince: 'Membre Depuis',
  },
  errors: {
    general: "Quelque chose s'est mal passé",
    network: 'Erreur réseau. Veuillez vérifier votre connexion',
    unauthorized: 'Accès non autorisé',
    forbidden: 'Accès interdit',
    notFound: 'Page non trouvée',
    serverError: 'Erreur serveur',
    tryAgain: 'Veuillez réessayer plus tard',
    sessionExpired: 'Session expirée. Veuillez vous reconnecter',
  },
  offline: {
    offline: 'Vous êtes hors ligne',
    online: 'Vous êtes de retour en ligne',
    syncPending: 'Les modifications seront synchronisées lorsque vous serez en ligne',
    syncing: 'Synchronisation...',
    synced: 'Toutes les modifications sont synchronisées',
  },
};

// Spanish translations
const esTranslations = {
  common: {
    appName: 'Tickally',
    loading: 'Cargando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    create: 'Crear',
    search: 'Buscar',
    filter: 'Filtrar',
    close: 'Cerrar',
    confirm: 'Confirmar',
    back: 'Atrás',
    next: 'Siguiente',
    previous: 'Anterior',
    submit: 'Enviar',
    success: 'Éxito',
    error: 'Error',
    warning: 'Advertencia',
    info: 'Info',
    yes: 'Sí',
    no: 'No',
    ok: 'OK',
    welcome: 'Bienvenido',
    logout: 'Cerrar Sesión',
    profile: 'Perfil',
    settings: 'Configuración',
    dashboard: 'Panel de Control',
    members: 'Miembros',
    attendance: 'Asistencia',
    dues: 'Cuotas',
    donations: 'Donaciones',
    finances: 'Finanzas',
    reports: 'Informes',
    total: 'Total',
    amount: 'Monto',
    date: 'Fecha',
    name: 'Nombre',
    fullName: 'Nombre Completo',
    email: 'Correo',
    phone: 'Teléfono',
    address: 'Dirección',
    status: 'Estado',
    active: 'Activo',
    inactive: 'Inactivo',
    pending: 'Pendiente',
    actions: 'Acciones',
    view: 'Ver',
    add: 'Agregar',
    remove: 'Eliminar',
    update: 'Actualizar',
    language: 'Idioma',
    currency: 'Moneda',
    app: {
      name: 'Tickally',
      tagline: 'Gestión de Organizaciones',
    },
    organization: {
      title: 'Organización',
      select: 'Seleccionar Organización',
      create: 'Crear Organización',
    },
    navigation: {
      dashboard: 'Panel de Control',
      members: 'Miembros',
      attendance: 'Asistencia',
      dues: 'Cuotas',
      donations: 'Donaciones',
      finances: 'Finanzas',
      subscription: 'Suscripción',
      settings: 'Configuración',
      myRecord: 'Mi Registro',
      profile: 'Perfil',
    },
    sidebar: {
      switchOrganization: 'Cambiar Organización',
    },
    role: 'Rol',
    admin: 'Administrador',
    treasurer: 'Tesorero',
    secretary: 'Secretario',
    member: 'Miembro',
    all: 'Todos',
    none: 'Ninguno',
    select: 'Seleccionar',
    selected: 'Seleccionado',
    noData: 'No hay datos disponibles',
    noResults: 'No se encontraron resultados',
    tryAgain: 'Intentar de nuevo',
    refresh: 'Actualizar',
    more: 'Más',
    less: 'Menos',
    show: 'Mostrar',
    hide: 'Ocultar',
    from: 'Desde',
    to: 'Hasta',
    of: 'de',
    items: 'elementos',
    perPage: 'por página',
    goToPage: 'Ir a la página',
    page: 'Página',
    pages: 'Páginas',
  },
  auth: {
    login: 'Iniciar Sesión',
    register: 'Registrarse',
    fullName: 'Nombre Completo',
    email: 'Correo',
    password: 'Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    resetPassword: 'Restablecer Contraseña',
    dontHaveAccount: '¿No tienes una cuenta?',
    alreadyHaveAccount: '¿Ya tienes una cuenta?',
    hasAccount: '¿Ya tienes una cuenta?',
    signUp: 'Registrarse',
    signIn: 'Iniciar Sesión',
    signOut: 'Cerrar Sesión',
    createAccount: 'Crear Cuenta',
    loginSuccess: 'Inicio de sesión exitoso',
    loginError: 'Error al iniciar sesión',
    registerSuccess: 'Registro exitoso',
    registerError: 'Error al registrarse',
    passwordMismatch: 'Las contraseñas no coinciden',
    invalidEmail: 'Correo inválido',
    weakPassword: 'Contraseña débil',
    requiredField: 'Este campo es obligatorio',
    firstName: 'Nombre',
    lastName: 'Apellido',
  },
  dashboard: {
    title: 'Panel de Control',
    welcome: 'Bienvenido de nuevo',
    overview: 'Resumen',
    quickStats: 'Estadísticas Rápidas',
    recentActivity: 'Actividad Reciente',
    totalMembers: 'Total de Miembros',
    activeMembers: 'Miembros Activos',
    attendanceToday: 'Asistencia Hoy',
    duesCollected: 'Cuotas Recaudadas',
    pendingDues: 'Cuotas Pendientes',
    totalDonations: 'Total de Donaciones',
    upcomingEvents: 'Próximos Eventos',
    recentPayments: 'Pagos Recientes',
    viewAll: 'Ver Todo',
    seeMore: 'Ver Más',
  },
  members: {
    title: 'Miembros',
    addMember: 'Agregar Miembro',
    editMember: 'Editar Miembro',
    deleteMember: 'Eliminar Miembro',
    memberDetails: 'Detalles del Miembro',
    memberList: 'Lista de Miembros',
    memberCard: 'Tarjeta de Miembro',
    firstName: 'Nombre',
    lastName: 'Apellido',
    fullName: 'Nombre Completo',
    email: 'Correo',
    phone: 'Teléfono',
    address: 'Dirección',
    joinDate: 'Fecha de Ingreso',
    status: 'Estado',
    active: 'Activo',
    inactive: 'Inactivo',
    searchMembers: 'Buscar miembros...',
    filterByStatus: 'Filtrar por estado',
    noMembers: 'No se encontraron miembros',
    memberSince: 'Miembro desde',
    lastAttendance: 'Última asistencia',
    totalAttendance: 'Asistencia total',
    duesPaid: 'Cuotas pagadas',
    duesOwed: 'Cuotas pendientes',
    confirmDelete: '¿Estás seguro de que deseas eliminar este miembro?',
    deleteSuccess: 'Miembro eliminado exitosamente',
    deleteError: 'Error al eliminar el miembro',
    saveSuccess: 'Miembro guardado exitosamente',
    saveError: 'Error al guardar el miembro',
  },
  attendance: {
    title: 'Asistencia',
    takeAttendance: 'Tomar Asistencia',
    meetingList: 'Lista de Reuniones',
    addMeeting: 'Agregar Reunión',
    editMeeting: 'Editar Reunión',
    meetingName: 'Nombre de la Reunión',
    meetingDate: 'Fecha de la Reunión',
    meetingType: 'Tipo de Reunión',
    present: 'Presente',
    absent: 'Ausente',
    late: 'Tarde',
    excused: 'Excusado',
    attendanceRate: 'Tasa de Asistencia',
    totalPresent: 'Total Presentes',
    totalAbsent: 'Total Ausentes',
    markAllPresent: 'Marcar Todos Presentes',
    markAllAbsent: 'Marcar Todos Ausentes',
    saveAttendance: 'Guardar Asistencia',
    attendanceSaved: 'Asistencia guardada exitosamente',
    attendanceError: 'Error al guardar la asistencia',
    noMeetings: 'No se encontraron reuniones',
    searchMeetings: 'Buscar reuniones...',
  },
  dues: {
    title: 'Cuotas',
    collectDues: 'Recaudar Cuotas',
    duesCollection: 'Recaudación de Cuotas',
    monthlyDues: 'Cuotas Mensuales',
    duesAmount: 'Monto de Cuotas',
    amountPaid: 'Monto Pagado',
    amountOwed: 'Monto Adeudado',
    paymentDate: 'Fecha de Pago',
    paymentMethod: 'Método de Pago',
    cash: 'Efectivo',
    bankTransfer: 'Transferencia Bancaria',
    mobileMoney: 'Dinero Móvil',
    check: 'Cheque',
    other: 'Otro',
    paymentFor: 'Pago Para',
    currentMonth: 'Mes Actual',
    previousMonths: 'Meses Anteriores',
    advancePayment: 'Pago Adelantado',
    backlog: 'Atrasos',
    prepay: 'Pago Anticipado',
    allocatePayment: 'Asignar Pago',
    allocationSuccess: 'Pago asignado exitosamente',
    allocationError: 'Error al asignar el pago',
    noDues: 'No hay registros de cuotas',
    memberDues: 'Cuotas del Miembro',
    allMembersDues: 'Cuotas de Todos los Miembros',
    paymentHistory: 'Historial de Pagos',
    totalCollected: 'Total Recaudado',
    totalPending: 'Total Pendiente',
    selectMember: 'Seleccionar Miembro',
    enterAmount: 'Ingresar Monto',
  },
  donations: {
    title: 'Donaciones',
    donationEvents: 'Eventos de Donación',
    addEvent: 'Agregar Evento',
    editEvent: 'Editar Evento',
    eventName: 'Nombre del Evento',
    eventDate: 'Fecha del Evento',
    eventDescription: 'Descripción',
    targetAmount: 'Monto Objetivo',
    collectedAmount: 'Monto Recaudado',
    remainingAmount: 'Monto Restante',
    donorName: 'Nombre del Donante',
    donorEmail: 'Correo del Donante',
    donorPhone: 'Teléfono del Donante',
    donationAmount: 'Monto de la Donación',
    addDonation: 'Agregar Donación',
    recordDonation: 'Registrar Donación',
    anonymous: 'Anónimo',
    eventStatus: 'Estado del Evento',
    ongoing: 'En Curso',
    completed: 'Completado',
    cancelled: 'Cancelado',
    noEvents: 'No se encontraron eventos de donación',
    noDonations: 'No hay donaciones registradas',
    deleteEventConfirm: '¿Estás seguro de que deseas eliminar este evento?',
    eventDeleted: 'Evento eliminado exitosamente',
    donationRecorded: 'Donación registrada exitosamente',
  },
  finances: {
    title: 'Finanzas',
    financialSummary: 'Resumen Financiero',
    income: 'Ingresos',
    expenses: 'Gastos',
    netIncome: 'Ingreso Neto',
    balance: 'Saldo',
    totalIncome: 'Ingresos Totales',
    totalExpenses: 'Gastos Totales',
    addExpense: 'Agregar Gasto',
    expenseCategory: 'Categoría',
    expenseDescription: 'Descripción',
    expenseAmount: 'Monto',
    expenseDate: 'Fecha',
    categories: {
      rent: 'Alquiler',
      utilities: 'Servicios',
      supplies: 'Suministros',
      maintenance: 'Mantenimiento',
      events: 'Eventos',
      salaries: 'Salarios',
      marketing: 'Marketing',
      other: 'Otro',
    },
    period: 'Período',
    thisMonth: 'Este Mes',
    lastMonth: 'Mes Pasado',
    thisQuarter: 'Este Trimestre',
    thisYear: 'Este Año',
    custom: 'Personalizado',
    generateReport: 'Generar Informe',
    exportReport: 'Exportar Informe',
    noTransactions: 'No se encontraron transacciones',
  },
  settings: {
    title: 'Configuración',
    organizationSettings: 'Configuración de la Organización',
    organizationName: 'Nombre de la Organización',
    organizationDescription: 'Descripción',
    organizationEmail: 'Correo de la Organización',
    organizationPhone: 'Teléfono de la Organización',
    organizationAddress: 'Dirección de la Organización',
    defaultCurrency: 'Moneda Predeterminada',
    defaultLanguage: 'Idioma Predeterminado',
    monthlyDuesAmount: 'Monto de Cuotas Mensuales',
    logo: 'Logo de la Organización',
    uploadLogo: 'Subir Logo',
    removeLogo: 'Eliminar Logo',
    userManagement: 'Gestión de Usuarios',
    inviteUser: 'Invitar Usuario',
    removeUser: 'Eliminar Usuario',
    changeRole: 'Cambiar Rol',
    appSettings: 'Configuración de la Aplicación',
    notifications: 'Notificaciones',
    emailNotifications: 'Notificaciones por Correo',
    pushNotifications: 'Notificaciones Push',
    darkMode: 'Modo Oscuro',
    saveSettings: 'Guardar Configuración',
    settingsSaved: 'Configuración guardada exitosamente',
    settingsError: 'Error al guardar la configuración',
  },
  memberPortal: {
    title: 'Mi Registro',
    myAttendance: 'Mi Asistencia',
    myDues: 'Mis Cuotas',
    myDonations: 'Mis Donaciones',
    personalInfo: 'Información Personal',
    attendanceHistory: 'Historial de Asistencia',
    duesHistory: 'Historial de Cuotas',
    paymentStatus: 'Estado de Pago',
    upToDate: 'Al Día',
    overdue: 'Atrasado',
    partial: 'Parcial',
    totalOwed: 'Total Adeudado',
    totalPaid: 'Total Pagado',
    nextPayment: 'Próximo Pago',
    memberSince: 'Miembro Desde',
  },
  errors: {
    general: 'Algo salió mal',
    network: 'Error de red. Por favor verifica tu conexión',
    unauthorized: 'Acceso no autorizado',
    forbidden: 'Acceso prohibido',
    notFound: 'Página no encontrada',
    serverError: 'Error del servidor',
    tryAgain: 'Por favor intenta de nuevo más tarde',
    sessionExpired: 'Sesión expirada. Por favor inicia sesión de nuevo',
  },
  offline: {
    offline: 'Estás desconectado',
    online: 'Estás de vuelta en línea',
    syncPending: 'Los cambios se sincronizarán cuando estés en línea',
    syncing: 'Sincronizando...',
    synced: 'Todos los cambios sincronizados',
  },
};

// Italian translations
const itTranslations = {
  common: {
    appName: 'Tickally',
    loading: 'Caricamento...',
    save: 'Salva',
    cancel: 'Annulla',
    delete: 'Elimina',
    edit: 'Modifica',
    create: 'Crea',
    search: 'Cerca',
    filter: 'Filtra',
    close: 'Chiudi',
    confirm: 'Conferma',
    back: 'Indietro',
    next: 'Avanti',
    previous: 'Precedente',
    submit: 'Invia',
    success: 'Successo',
    error: 'Errore',
    warning: 'Avviso',
    info: 'Info',
    yes: 'Sì',
    no: 'No',
    ok: 'OK',
    welcome: 'Benvenuto',
    logout: 'Disconnetti',
    profile: 'Profilo',
    settings: 'Impostazioni',
    dashboard: 'Dashboard',
    members: 'Membri',
    attendance: 'Presenze',
    dues: 'Quote',
    donations: 'Donazioni',
    finances: 'Finanze',
    reports: 'Rapporti',
    total: 'Totale',
    amount: 'Importo',
    date: 'Data',
    name: 'Nome',
    fullName: 'Nome Completo',
    email: 'Email',
    phone: 'Telefono',
    address: 'Indirizzo',
    status: 'Stato',
    active: 'Attivo',
    inactive: 'Inattivo',
    pending: 'In attesa',
    actions: 'Azioni',
    view: 'Visualizza',
    add: 'Aggiungi',
    remove: 'Rimuovi',
    update: 'Aggiorna',
    language: 'Lingua',
    currency: 'Valuta',
    app: {
      name: 'Tickally',
      tagline: 'Gestione Organizzazioni',
    },
    organization: {
      title: 'Organizzazione',
      select: 'Seleziona Organizzazione',
      create: 'Crea Organizzazione',
    },
    navigation: {
      dashboard: 'Dashboard',
      members: 'Membri',
      attendance: 'Presenze',
      dues: 'Quote',
      donations: 'Donazioni',
      finances: 'Finanze',
      subscription: 'Abbonamento',
      settings: 'Impostazioni',
      myRecord: 'Il Mio Registro',
      profile: 'Profilo',
    },
    sidebar: {
      switchOrganization: 'Cambia Organizzazione',
    },
    role: 'Ruolo',
    admin: 'Amministratore',
    treasurer: 'Tesoriere',
    secretary: 'Segretario',
    member: 'Membro',
    all: 'Tutti',
    none: 'Nessuno',
    select: 'Seleziona',
    selected: 'Selezionato',
    noData: 'Nessun dato disponibile',
    noResults: 'Nessun risultato trovato',
    tryAgain: 'Riprova',
    refresh: 'Aggiorna',
    more: 'Più',
    less: 'Meno',
    show: 'Mostra',
    hide: 'Nascondi',
    from: 'Da',
    to: 'A',
    of: 'di',
    items: 'elementi',
    perPage: 'per pagina',
    goToPage: 'Vai alla pagina',
    page: 'Pagina',
    pages: 'Pagine',
  },
  auth: {
    login: 'Accedi',
    register: 'Registrati',
    fullName: 'Nome Completo',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Conferma Password',
    forgotPassword: 'Password dimenticata?',
    resetPassword: 'Reimposta Password',
    dontHaveAccount: 'Non hai un account?',
    alreadyHaveAccount: 'Hai già un account?',
    hasAccount: 'Hai già un account?',
    signUp: 'Iscriviti',
    signIn: 'Accedi',
    signOut: 'Disconnetti',
    createAccount: 'Crea Account',
    loginSuccess: 'Accesso effettuato con successo',
    loginError: 'Accesso fallito',
    registerSuccess: 'Registrazione completata con successo',
    registerError: 'Registrazione fallita',
    passwordMismatch: 'Le password non corrispondono',
    invalidEmail: 'Indirizzo email non valido',
    weakPassword: 'Password troppo debole',
    requiredField: 'Questo campo è obbligatorio',
    firstName: 'Nome',
    lastName: 'Cognome',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Bentornato',
    overview: 'Panoramica',
    quickStats: 'Statistiche Rapide',
    recentActivity: 'Attività Recente',
    totalMembers: 'Totale Membri',
    activeMembers: 'Membri Attivi',
    attendanceToday: 'Presenze Oggi',
    duesCollected: 'Quote Riscosse',
    pendingDues: 'Quote in Attesa',
    totalDonations: 'Totale Donazioni',
    upcomingEvents: 'Prossimi Eventi',
    recentPayments: 'Pagamenti Recenti',
    viewAll: 'Vedi Tutto',
    seeMore: 'Vedi Altro',
  },
  members: {
    title: 'Membri',
    addMember: 'Aggiungi Membro',
    editMember: 'Modifica Membro',
    deleteMember: 'Elimina Membro',
    memberDetails: 'Dettagli Membro',
    memberList: 'Lista Membri',
    memberCard: 'Tessera Membro',
    firstName: 'Nome',
    lastName: 'Cognome',
    fullName: 'Nome Completo',
    email: 'Email',
    phone: 'Numero di Telefono',
    address: 'Indirizzo',
    joinDate: 'Data di Iscrizione',
    status: 'Stato',
    active: 'Attivo',
    inactive: 'Inattivo',
    searchMembers: 'Cerca membri...',
    filterByStatus: 'Filtra per stato',
    noMembers: 'Nessun membro trovato',
    memberSince: 'Membro dal',
    lastAttendance: 'Ultima presenza',
    totalAttendance: 'Presenze totali',
    duesPaid: 'Quote pagate',
    duesOwed: 'Quote dovute',
    confirmDelete: 'Sei sicuro di voler eliminare questo membro?',
    deleteSuccess: 'Membro eliminato con successo',
    deleteError: 'Impossibile eliminare il membro',
    saveSuccess: 'Membro salvato con successo',
    saveError: 'Impossibile salvare il membro',
  },
  attendance: {
    title: 'Presenze',
    takeAttendance: 'Registra Presenze',
    meetingList: 'Lista Riunioni',
    addMeeting: 'Aggiungi Riunione',
    editMeeting: 'Modifica Riunione',
    meetingName: 'Nome Riunione',
    meetingDate: 'Data Riunione',
    meetingType: 'Tipo Riunione',
    present: 'Presente',
    absent: 'Assente',
    late: 'In ritardo',
    excused: 'Giustificato',
    attendanceRate: 'Tasso di Presenza',
    totalPresent: 'Totale Presenti',
    totalAbsent: 'Totale Assenti',
    markAllPresent: 'Segna Tutti Presenti',
    markAllAbsent: 'Segna Tutti Assenti',
    saveAttendance: 'Salva Presenze',
    attendanceSaved: 'Presenze salvate con successo',
    attendanceError: 'Impossibile salvare le presenze',
    noMeetings: 'Nessuna riunione trovata',
    searchMeetings: 'Cerca riunioni...',
  },
  dues: {
    title: 'Quote',
    collectDues: 'Riscuoti Quote',
    duesCollection: 'Riscossione Quote',
    monthlyDues: 'Quote Mensili',
    duesAmount: 'Importo Quote',
    amountPaid: 'Importo Pagato',
    amountOwed: 'Importo Dovuto',
    paymentDate: 'Data Pagamento',
    paymentMethod: 'Metodo di Pagamento',
    cash: 'Contanti',
    bankTransfer: 'Bonifico Bancario',
    mobileMoney: 'Mobile Money',
    check: 'Assegno',
    other: 'Altro',
    paymentFor: 'Pagamento Per',
    currentMonth: 'Mese Corrente',
    previousMonths: 'Mesi Precedenti',
    advancePayment: 'Pagamento Anticipato',
    backlog: 'Arretrati',
    prepay: 'Pagamento Anticipato',
    allocatePayment: 'Alloca Pagamento',
    allocationSuccess: 'Pagamento allocato con successo',
    allocationError: 'Impossibile allocare il pagamento',
    noDues: 'Nessun record di quote',
    memberDues: 'Quote Membro',
    allMembersDues: 'Quote di Tutti i Membri',
    paymentHistory: 'Cronologia Pagamenti',
    totalCollected: 'Totale Riscosso',
    totalPending: 'Totale in Attesa',
    selectMember: 'Seleziona Membro',
    enterAmount: 'Inserisci Importo',
  },
  donations: {
    title: 'Donazioni',
    donationEvents: 'Eventi di Donazione',
    addEvent: 'Aggiungi Evento',
    editEvent: 'Modifica Evento',
    eventName: 'Nome Evento',
    eventDate: 'Data Evento',
    eventDescription: 'Descrizione',
    targetAmount: 'Importo Obiettivo',
    collectedAmount: 'Importo Raccolto',
    remainingAmount: 'Importo Rimanente',
    donorName: 'Nome Donatore',
    donorEmail: 'Email Donatore',
    donorPhone: 'Telefono Donatore',
    donationAmount: 'Importo Donazione',
    addDonation: 'Aggiungi Donazione',
    recordDonation: 'Registra Donazione',
    anonymous: 'Anonimo',
    eventStatus: 'Stato Evento',
    ongoing: 'In Corso',
    completed: 'Completato',
    cancelled: 'Annullato',
    noEvents: 'Nessun evento di donazione trovato',
    noDonations: 'Nessuna donazione registrata',
    deleteEventConfirm: 'Sei sicuro di voler eliminare questo evento?',
    eventDeleted: 'Evento eliminato con successo',
    donationRecorded: 'Donazione registrata con successo',
  },
  finances: {
    title: 'Finanze',
    financialSummary: 'Riepilogo Finanziario',
    income: 'Entrate',
    expenses: 'Uscite',
    netIncome: 'Utile Netto',
    balance: 'Saldo',
    totalIncome: 'Entrate Totali',
    totalExpenses: 'Uscite Totali',
    addExpense: 'Aggiungi Spesa',
    expenseCategory: 'Categoria',
    expenseDescription: 'Descrizione',
    expenseAmount: 'Importo',
    expenseDate: 'Data',
    categories: {
      rent: 'Affitto',
      utilities: 'Servizi',
      supplies: 'Forniture',
      maintenance: 'Manutenzione',
      events: 'Eventi',
      salaries: 'Stipendi',
      marketing: 'Marketing',
      other: 'Altro',
    },
    period: 'Periodo',
    thisMonth: 'Questo Mese',
    lastMonth: 'Mese Scorso',
    thisQuarter: 'Questo Trimestre',
    thisYear: 'Questo Anno',
    custom: 'Personalizzato',
    generateReport: 'Genera Rapporto',
    exportReport: 'Esporta Rapporto',
    noTransactions: 'Nessuna transazione trovata',
  },
  settings: {
    title: 'Impostazioni',
    organizationSettings: 'Impostazioni Organizzazione',
    organizationName: 'Nome Organizzazione',
    organizationDescription: 'Descrizione',
    organizationEmail: 'Email Organizzazione',
    organizationPhone: 'Telefono Organizzazione',
    organizationAddress: 'Indirizzo Organizzazione',
    defaultCurrency: 'Valuta Predefinita',
    defaultLanguage: 'Lingua Predefinita',
    monthlyDuesAmount: 'Importo Quote Mensili',
    logo: 'Logo Organizzazione',
    uploadLogo: 'Carica Logo',
    removeLogo: 'Rimuovi Logo',
    userManagement: 'Gestione Utenti',
    inviteUser: 'Invita Utente',
    removeUser: 'Rimuovi Utente',
    changeRole: 'Cambia Ruolo',
    appSettings: 'Impostazioni App',
    notifications: 'Notifiche',
    emailNotifications: 'Notifiche Email',
    pushNotifications: 'Notifiche Push',
    darkMode: 'Modalità Scura',
    saveSettings: 'Salva Impostazioni',
    settingsSaved: 'Impostazioni salvate con successo',
    settingsError: 'Impossibile salvare le impostazioni',
  },
  memberPortal: {
    title: 'Mia Scheda',
    myAttendance: 'Mie Presenze',
    myDues: 'Mie Quote',
    myDonations: 'Mie Donazioni',
    personalInfo: 'Informazioni Personali',
    attendanceHistory: 'Cronologia Presenze',
    duesHistory: 'Cronologia Quote',
    paymentStatus: 'Stato Pagamento',
    upToDate: 'Aggiornato',
    overdue: 'In Ritardo',
    partial: 'Parziale',
    totalOwed: 'Totale Dovuto',
    totalPaid: 'Totale Pagato',
    nextPayment: 'Prossimo Pagamento',
    memberSince: 'Membro Dal',
  },
  errors: {
    general: 'Qualcosa è andato storto',
    network: 'Errore di rete. Verifica la tua connessione',
    unauthorized: 'Accesso non autorizzato',
    forbidden: 'Accesso vietato',
    notFound: 'Pagina non trovata',
    serverError: 'Errore del server',
    tryAgain: 'Riprova più tardi',
    sessionExpired: 'Sessione scaduta. Accedi di nuovo',
  },
  offline: {
    offline: 'Sei offline',
    online: 'Sei tornato online',
    syncPending: 'Le modifiche verranno sincronizzate quando sei online',
    syncing: 'Sincronizzazione...',
    synced: 'Tutte le modifiche sincronizzate',
  },
};

// German translations
const deTranslations = {
  common: {
    appName: 'Tickally',
    loading: 'Laden...',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    create: 'Erstellen',
    search: 'Suchen',
    filter: 'Filtern',
    close: 'Schließen',
    confirm: 'Bestätigen',
    back: 'Zurück',
    next: 'Weiter',
    previous: 'Zurück',
    submit: 'Absenden',
    success: 'Erfolg',
    error: 'Fehler',
    warning: 'Warnung',
    info: 'Info',
    yes: 'Ja',
    no: 'Nein',
    ok: 'OK',
    welcome: 'Willkommen',
    logout: 'Abmelden',
    profile: 'Profil',
    settings: 'Einstellungen',
    dashboard: 'Dashboard',
    members: 'Mitglieder',
    attendance: 'Anwesenheit',
    dues: 'Beiträge',
    donations: 'Spenden',
    finances: 'Finanzen',
    reports: 'Berichte',
    total: 'Gesamt',
    amount: 'Betrag',
    date: 'Datum',
    name: 'Name',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    phone: 'Telefon',
    address: 'Adresse',
    status: 'Status',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    pending: 'Ausstehend',
    actions: 'Aktionen',
    view: 'Ansehen',
    add: 'Hinzufügen',
    remove: 'Entfernen',
    update: 'Aktualisieren',
    language: 'Sprache',
    currency: 'Währung',
    app: {
      name: 'Tickally',
      tagline: 'Organisationsverwaltung',
    },
    organization: {
      title: 'Organisation',
      select: 'Organisation Auswählen',
      create: 'Organisation Erstellen',
    },
    navigation: {
      dashboard: 'Dashboard',
      members: 'Mitglieder',
      attendance: 'Anwesenheit',
      dues: 'Beiträge',
      donations: 'Spenden',
      finances: 'Finanzen',
      subscription: 'Abonnement',
      settings: 'Einstellungen',
      myRecord: 'Mein Eintrag',
      profile: 'Profil',
    },
    sidebar: {
      switchOrganization: 'Organisation Wechseln',
    },
    role: 'Rolle',
    admin: 'Administrator',
    treasurer: 'Schatzmeister',
    secretary: 'Sekretär',
    member: 'Mitglied',
    all: 'Alle',
    none: 'Keine',
    select: 'Auswählen',
    selected: 'Ausgewählt',
    noData: 'Keine Daten verfügbar',
    noResults: 'Keine Ergebnisse gefunden',
    tryAgain: 'Erneut versuchen',
    refresh: 'Aktualisieren',
    more: 'Mehr',
    less: 'Weniger',
    show: 'Anzeigen',
    hide: 'Ausblenden',
    from: 'Von',
    to: 'Bis',
    of: 'von',
    items: 'Einträge',
    perPage: 'pro Seite',
    goToPage: 'Zur Seite gehen',
    page: 'Seite',
    pages: 'Seiten',
  },
  auth: {
    login: 'Anmelden',
    register: 'Registrieren',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    password: 'Passwort',
    confirmPassword: 'Passwort bestätigen',
    forgotPassword: 'Passwort vergessen?',
    resetPassword: 'Passwort zurücksetzen',
    dontHaveAccount: 'Kein Konto?',
    alreadyHaveAccount: 'Bereits ein Konto?',
    hasAccount: 'Bereits ein Konto?',
    signUp: 'Registrieren',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    createAccount: 'Konto erstellen',
    loginSuccess: 'Anmeldung erfolgreich',
    loginError: 'Anmeldung fehlgeschlagen',
    registerSuccess: 'Registrierung erfolgreich',
    registerError: 'Registrierung fehlgeschlagen',
    passwordMismatch: 'Passwörter stimmen nicht überein',
    invalidEmail: 'Ungültige E-Mail-Adresse',
    weakPassword: 'Passwort zu schwach',
    requiredField: 'Dieses Feld ist erforderlich',
    firstName: 'Vorname',
    lastName: 'Nachname',
  },
  dashboard: {
    title: 'Dashboard',
    welcome: 'Willkommen zurück',
    overview: 'Übersicht',
    quickStats: 'Schnellstatistiken',
    recentActivity: 'Letzte Aktivität',
    totalMembers: 'Mitglieder gesamt',
    activeMembers: 'Aktive Mitglieder',
    attendanceToday: 'Anwesenheit heute',
    duesCollected: 'Beiträge eingezogen',
    pendingDues: 'Ausstehende Beiträge',
    totalDonations: 'Spenden gesamt',
    upcomingEvents: 'Kommende Veranstaltungen',
    recentPayments: 'Letzte Zahlungen',
    viewAll: 'Alle anzeigen',
    seeMore: 'Mehr anzeigen',
  },
  members: {
    title: 'Mitglieder',
    addMember: 'Mitglied hinzufügen',
    editMember: 'Mitglied bearbeiten',
    deleteMember: 'Mitglied löschen',
    memberDetails: 'Mitgliederdetails',
    memberList: 'Mitgliederliste',
    memberCard: 'Mitgliedskarte',
    firstName: 'Vorname',
    lastName: 'Nachname',
    fullName: 'Vollständiger Name',
    email: 'E-Mail',
    phone: 'Telefonnummer',
    address: 'Adresse',
    joinDate: 'Beitrittsdatum',
    status: 'Status',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    searchMembers: 'Mitglieder suchen...',
    filterByStatus: 'Nach Status filtern',
    noMembers: 'Keine Mitglieder gefunden',
    memberSince: 'Mitglied seit',
    lastAttendance: 'Letzte Anwesenheit',
    totalAttendance: 'Gesamtanwesenheit',
    duesPaid: 'Beiträge bezahlt',
    duesOwed: 'Beiträge geschuldet',
    confirmDelete: 'Möchten Sie dieses Mitglied wirklich löschen?',
    deleteSuccess: 'Mitglied erfolgreich gelöscht',
    deleteError: 'Mitglied konnte nicht gelöscht werden',
    saveSuccess: 'Mitglied erfolgreich gespeichert',
    saveError: 'Mitglied konnte nicht gespeichert werden',
  },
  attendance: {
    title: 'Anwesenheit',
    takeAttendance: 'Anwesenheit erfassen',
    meetingList: 'Besprechungsliste',
    addMeeting: 'Besprechung hinzufügen',
    editMeeting: 'Besprechung bearbeiten',
    meetingName: 'Besprechungsname',
    meetingDate: 'Besprechungsdatum',
    meetingType: 'Besprechungstyp',
    present: 'Anwesend',
    absent: 'Abwesend',
    late: 'Verspätet',
    excused: 'Entschuldigt',
    attendanceRate: 'Anwesenheitsrate',
    totalPresent: 'Gesamt anwesend',
    totalAbsent: 'Gesamt abwesend',
    markAllPresent: 'Alle als anwesend markieren',
    markAllAbsent: 'Alle als abwesend markieren',
    saveAttendance: 'Anwesenheit speichern',
    attendanceSaved: 'Anwesenheit erfolgreich gespeichert',
    attendanceError: 'Anwesenheit konnte nicht gespeichert werden',
    noMeetings: 'Keine Besprechungen gefunden',
    searchMeetings: 'Besprechungen suchen...',
  },
  dues: {
    title: 'Beiträge',
    collectDues: 'Beiträge einsammeln',
    duesCollection: 'Beitragseinzug',
    monthlyDues: 'Monatliche Beiträge',
    duesAmount: 'Beitragsbetrag',
    amountPaid: 'Gezahlter Betrag',
    amountOwed: 'Geschuldeter Betrag',
    paymentDate: 'Zahlungsdatum',
    paymentMethod: 'Zahlungsmethode',
    cash: 'Bar',
    bankTransfer: 'Banküberweisung',
    mobileMoney: 'Mobiles Geld',
    check: 'Scheck',
    other: 'Andere',
    paymentFor: 'Zahlung für',
    currentMonth: 'Aktueller Monat',
    previousMonths: 'Vorherige Monate',
    advancePayment: 'Vorauszahlung',
    backlog: 'Rückstand',
    prepay: 'Vorauszahlung',
    allocatePayment: 'Zahlung zuweisen',
    allocationSuccess: 'Zahlung erfolgreich zugewiesen',
    allocationError: 'Zahlung konnte nicht zugewiesen werden',
    noDues: 'Keine Beitragsaufzeichnungen',
    memberDues: 'Mitgliederbeiträge',
    allMembersDues: 'Beiträge aller Mitglieder',
    paymentHistory: 'Zahlungshistorie',
    totalCollected: 'Gesamt eingezogen',
    totalPending: 'Gesamt ausstehend',
    selectMember: 'Mitglied auswählen',
    enterAmount: 'Betrag eingeben',
  },
  donations: {
    title: 'Spenden',
    donationEvents: 'Spendenveranstaltungen',
    addEvent: 'Veranstaltung hinzufügen',
    editEvent: 'Veranstaltung bearbeiten',
    eventName: 'Veranstaltungsname',
    eventDate: 'Veranstaltungsdatum',
    eventDescription: 'Beschreibung',
    targetAmount: 'Zielbetrag',
    collectedAmount: 'Gesammelter Betrag',
    remainingAmount: 'Verbleibender Betrag',
    donorName: 'Spendername',
    donorEmail: 'Spender-E-Mail',
    donorPhone: 'Spender-Telefon',
    donationAmount: 'Spendenbetrag',
    addDonation: 'Spende hinzufügen',
    recordDonation: 'Spende erfassen',
    anonymous: 'Anonym',
    eventStatus: 'Veranstaltungsstatus',
    ongoing: 'Laufend',
    completed: 'Abgeschlossen',
    cancelled: 'Abgebrochen',
    noEvents: 'Keine Spendenveranstaltungen gefunden',
    noDonations: 'Keine Spenden erfasst',
    deleteEventConfirm: 'Möchten Sie diese Veranstaltung wirklich löschen?',
    eventDeleted: 'Veranstaltung erfolgreich gelöscht',
    donationRecorded: 'Spende erfolgreich erfasst',
  },
  finances: {
    title: 'Finanzen',
    financialSummary: 'Finanzübersicht',
    income: 'Einnahmen',
    expenses: 'Ausgaben',
    netIncome: 'Nettoeinkommen',
    balance: 'Saldo',
    totalIncome: 'Gesamteinnahmen',
    totalExpenses: 'Gesamtausgaben',
    addExpense: 'Ausgabe hinzufügen',
    expenseCategory: 'Kategorie',
    expenseDescription: 'Beschreibung',
    expenseAmount: 'Betrag',
    expenseDate: 'Datum',
    categories: {
      rent: 'Miete',
      utilities: 'Nebenkosten',
      supplies: 'Büromaterial',
      maintenance: 'Wartung',
      events: 'Veranstaltungen',
      salaries: 'Gehälter',
      marketing: 'Marketing',
      other: 'Andere',
    },
    period: 'Zeitraum',
    thisMonth: 'Dieser Monat',
    lastMonth: 'Letzter Monat',
    thisQuarter: 'Dieses Quartal',
    thisYear: 'Dieses Jahr',
    custom: 'Benutzerdefiniert',
    generateReport: 'Bericht erstellen',
    exportReport: 'Bericht exportieren',
    noTransactions: 'Keine Transaktionen gefunden',
  },
  settings: {
    title: 'Einstellungen',
    organizationSettings: 'Organisationseinstellungen',
    organizationName: 'Organisationsname',
    organizationDescription: 'Beschreibung',
    organizationEmail: 'Organisations-E-Mail',
    organizationPhone: 'Organisationstelefon',
    organizationAddress: 'Organisationsadresse',
    defaultCurrency: 'Standardwährung',
    defaultLanguage: 'Standardsprache',
    monthlyDuesAmount: 'Monatlicher Beitragsbetrag',
    logo: 'Organisationslogo',
    uploadLogo: 'Logo hochladen',
    removeLogo: 'Logo entfernen',
    userManagement: 'Benutzerverwaltung',
    inviteUser: 'Benutzer einladen',
    removeUser: 'Benutzer entfernen',
    changeRole: 'Rolle ändern',
    appSettings: 'App-Einstellungen',
    notifications: 'Benachrichtigungen',
    emailNotifications: 'E-Mail-Benachrichtigungen',
    pushNotifications: 'Push-Benachrichtigungen',
    darkMode: 'Dunkelmodus',
    saveSettings: 'Einstellungen speichern',
    settingsSaved: 'Einstellungen erfolgreich gespeichert',
    settingsError: 'Einstellungen konnten nicht gespeichert werden',
  },
  memberPortal: {
    title: 'Meine Akte',
    myAttendance: 'Meine Anwesenheit',
    myDues: 'Meine Beiträge',
    myDonations: 'Meine Spenden',
    personalInfo: 'Persönliche Informationen',
    attendanceHistory: 'Anwesenheitshistorie',
    duesHistory: 'Beitragshistorie',
    paymentStatus: 'Zahlungsstatus',
    upToDate: 'Aktuell',
    overdue: 'Überfällig',
    partial: 'Teilweise',
    totalOwed: 'Gesamtschuld',
    totalPaid: 'Gesamt bezahlt',
    nextPayment: 'Nächste Zahlung',
    memberSince: 'Mitglied seit',
  },
  errors: {
    general: 'Etwas ist schief gelaufen',
    network: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Verbindung',
    unauthorized: 'Unautorisierter Zugriff',
    forbidden: 'Zugriff verboten',
    notFound: 'Seite nicht gefunden',
    serverError: 'Serverfehler',
    tryAgain: 'Bitte versuchen Sie es später erneut',
    sessionExpired: 'Sitzung abgelaufen. Bitte melden Sie sich erneut an',
  },
  offline: {
    offline: 'Sie sind offline',
    online: 'Sie sind wieder online',
    syncPending: 'Änderungen werden synchronisiert, wenn Sie online sind',
    syncing: 'Synchronisierung...',
    synced: 'Alle Änderungen synchronisiert',
  },
};

// Mandarin Chinese translations
const zhTranslations = {
  common: {
    appName: 'Tickally',
    loading: '加载中...',
    save: '保存',
    cancel: '取消',
    delete: '删除',
    edit: '编辑',
    create: '创建',
    search: '搜索',
    filter: '筛选',
    close: '关闭',
    confirm: '确认',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    submit: '提交',
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息',
    yes: '是',
    no: '否',
    ok: '确定',
    welcome: '欢迎',
    logout: '退出登录',
    profile: '个人资料',
    settings: '设置',
    dashboard: '仪表板',
    members: '成员',
    attendance: '出勤',
    dues: '会费',
    donations: '捐赠',
    finances: '财务',
    reports: '报告',
    total: '总计',
    amount: '金额',
    date: '日期',
    name: '姓名',
    fullName: '全名',
    email: '电子邮箱',
    phone: '电话',
    address: '地址',
    status: '状态',
    active: '活跃',
    inactive: '非活跃',
    pending: '待处理',
    actions: '操作',
    view: '查看',
    add: '添加',
    remove: '移除',
    update: '更新',
    language: '语言',
    currency: '货币',
    app: {
      name: 'Tickally',
      tagline: '组织管理',
    },
    organization: {
      title: '组织',
      select: '选择组织',
      create: '创建组织',
    },
    navigation: {
      dashboard: '仪表板',
      members: '成员',
      attendance: '出勤',
      dues: '会费',
      donations: '捐赠',
      finances: '财务',
      subscription: '订阅',
      settings: '设置',
      myRecord: '我的记录',
      profile: '个人资料',
    },
    sidebar: {
      switchOrganization: '切换组织',
    },
    role: '角色',
    admin: '管理员',
    treasurer: '财务主管',
    secretary: '秘书',
    member: '成员',
    all: '全部',
    none: '无',
    select: '选择',
    selected: '已选择',
    noData: '无可用数据',
    noResults: '未找到结果',
    tryAgain: '重试',
    refresh: '刷新',
    more: '更多',
    less: '更少',
    show: '显示',
    hide: '隐藏',
    from: '从',
    to: '到',
    of: '的',
    items: '项目',
    perPage: '每页',
    goToPage: '跳转到页面',
    page: '页',
    pages: '页数',
  },
  auth: {
    login: '登录',
    register: '注册',
    fullName: '全名',
    email: '电子邮箱',
    password: '密码',
    confirmPassword: '确认密码',
    forgotPassword: '忘记密码？',
    resetPassword: '重置密码',
    dontHaveAccount: '没有账户？',
    alreadyHaveAccount: '已有账户？',
    hasAccount: '已有账户？',
    signUp: '注册',
    signIn: '登录',
    signOut: '退出登录',
    createAccount: '创建账户',
    loginSuccess: '登录成功',
    loginError: '登录失败',
    registerSuccess: '注册成功',
    registerError: '注册失败',
    passwordMismatch: '密码不匹配',
    invalidEmail: '电子邮箱地址无效',
    weakPassword: '密码太弱',
    requiredField: '此字段为必填项',
    firstName: '名字',
    lastName: '姓氏',
  },
  dashboard: {
    title: '仪表板',
    welcome: '欢迎回来',
    overview: '概览',
    quickStats: '快速统计',
    recentActivity: '最近活动',
    totalMembers: '成员总数',
    activeMembers: '活跃成员',
    attendanceToday: '今日出勤',
    duesCollected: '已收会费',
    pendingDues: '待收会费',
    totalDonations: '捐赠总额',
    upcomingEvents: '即将举行的活动',
    recentPayments: '最近付款',
    viewAll: '查看全部',
    seeMore: '查看更多',
  },
  members: {
    title: '成员',
    addMember: '添加成员',
    editMember: '编辑成员',
    deleteMember: '删除成员',
    memberDetails: '成员详情',
    memberList: '成员列表',
    memberCard: '成员卡',
    firstName: '名字',
    lastName: '姓氏',
    fullName: '全名',
    email: '电子邮箱',
    phone: '电话号码',
    address: '地址',
    joinDate: '加入日期',
    status: '状态',
    active: '活跃',
    inactive: '非活跃',
    searchMembers: '搜索成员...',
    filterByStatus: '按状态筛选',
    noMembers: '未找到成员',
    memberSince: '加入时间',
    lastAttendance: '上次出勤',
    totalAttendance: '总出勤',
    duesPaid: '已付会费',
    duesOwed: '欠费',
    confirmDelete: '确定要删除此成员吗？',
    deleteSuccess: '成员删除成功',
    deleteError: '删除成员失败',
    saveSuccess: '成员保存成功',
    saveError: '保存成员失败',
  },
  attendance: {
    title: '出勤',
    takeAttendance: '记录出勤',
    meetingList: '会议列表',
    addMeeting: '添加会议',
    editMeeting: '编辑会议',
    meetingName: '会议名称',
    meetingDate: '会议日期',
    meetingType: '会议类型',
    present: '出席',
    absent: '缺席',
    late: '迟到',
    excused: '请假',
    attendanceRate: '出勤率',
    totalPresent: '总出席',
    totalAbsent: '总缺席',
    markAllPresent: '标记全部出席',
    markAllAbsent: '标记全部缺席',
    saveAttendance: '保存出勤',
    attendanceSaved: '出勤保存成功',
    attendanceError: '保存出勤失败',
    noMeetings: '未找到会议',
    searchMeetings: '搜索会议...',
  },
  dues: {
    title: '会费',
    collectDues: '收取会费',
    duesCollection: '会费收取',
    monthlyDues: '月度会费',
    duesAmount: '会费金额',
    amountPaid: '已付金额',
    amountOwed: '欠款金额',
    paymentDate: '付款日期',
    paymentMethod: '付款方式',
    cash: '现金',
    bankTransfer: '银行转账',
    mobileMoney: '移动支付',
    check: '支票',
    other: '其他',
    paymentFor: '付款用途',
    currentMonth: '当月',
    previousMonths: '前几个月',
    advancePayment: '预付款',
    backlog: '欠款',
    prepay: '预付',
    allocatePayment: '分配付款',
    allocationSuccess: '付款分配成功',
    allocationError: '付款分配失败',
    noDues: '无会费记录',
    memberDues: '成员会费',
    allMembersDues: '所有成员会费',
    paymentHistory: '付款历史',
    totalCollected: '已收总额',
    totalPending: '待收总额',
    selectMember: '选择成员',
    enterAmount: '输入金额',
  },
  donations: {
    title: '捐赠',
    donationEvents: '捐赠活动',
    addEvent: '添加活动',
    editEvent: '编辑活动',
    eventName: '活动名称',
    eventDate: '活动日期',
    eventDescription: '描述',
    targetAmount: '目标金额',
    collectedAmount: '已收金额',
    remainingAmount: '剩余金额',
    donorName: '捐赠者姓名',
    donorEmail: '捐赠者邮箱',
    donorPhone: '捐赠者电话',
    donationAmount: '捐赠金额',
    addDonation: '添加捐赠',
    recordDonation: '记录捐赠',
    anonymous: '匿名',
    eventStatus: '活动状态',
    ongoing: '进行中',
    completed: '已完成',
    cancelled: '已取消',
    noEvents: '未找到捐赠活动',
    noDonations: '未记录捐赠',
    deleteEventConfirm: '确定要删除此活动吗？',
    eventDeleted: '活动删除成功',
    donationRecorded: '捐赠记录成功',
  },
  finances: {
    title: '财务',
    financialSummary: '财务摘要',
    income: '收入',
    expenses: '支出',
    netIncome: '净收入',
    balance: '余额',
    totalIncome: '总收入',
    totalExpenses: '总支出',
    addExpense: '添加支出',
    expenseCategory: '类别',
    expenseDescription: '描述',
    expenseAmount: '金额',
    expenseDate: '日期',
    categories: {
      rent: '租金',
      utilities: '水电费',
      supplies: '用品',
      maintenance: '维护',
      events: '活动',
      salaries: '工资',
      marketing: '营销',
      other: '其他',
    },
    period: '期间',
    thisMonth: '本月',
    lastMonth: '上月',
    thisQuarter: '本季度',
    thisYear: '今年',
    custom: '自定义',
    generateReport: '生成报告',
    exportReport: '导出报告',
    noTransactions: '未找到交易',
  },
  settings: {
    title: '设置',
    organizationSettings: '组织设置',
    organizationName: '组织名称',
    organizationDescription: '描述',
    organizationEmail: '组织邮箱',
    organizationPhone: '组织电话',
    organizationAddress: '组织地址',
    defaultCurrency: '默认货币',
    defaultLanguage: '默认语言',
    monthlyDuesAmount: '月度会费金额',
    logo: '组织标志',
    uploadLogo: '上传标志',
    removeLogo: '移除标志',
    userManagement: '用户管理',
    inviteUser: '邀请用户',
    removeUser: '移除用户',
    changeRole: '更改角色',
    appSettings: '应用设置',
    notifications: '通知',
    emailNotifications: '邮件通知',
    pushNotifications: '推送通知',
    darkMode: '深色模式',
    saveSettings: '保存设置',
    settingsSaved: '设置保存成功',
    settingsError: '保存设置失败',
  },
  memberPortal: {
    title: '我的记录',
    myAttendance: '我的出勤',
    myDues: '我的会费',
    myDonations: '我的捐赠',
    personalInfo: '个人信息',
    attendanceHistory: '出勤历史',
    duesHistory: '会费历史',
    paymentStatus: '付款状态',
    upToDate: '最新',
    overdue: '逾期',
    partial: '部分',
    totalOwed: '欠款总额',
    totalPaid: '已付总额',
    nextPayment: '下次付款',
    memberSince: '加入时间',
  },
  errors: {
    general: '出了点问题',
    network: '网络错误。请检查您的连接',
    unauthorized: '未经授权的访问',
    forbidden: '访问被禁止',
    notFound: '页面未找到',
    serverError: '服务器错误',
    tryAgain: '请稍后再试',
    sessionExpired: '会话已过期。请重新登录',
  },
  offline: {
    offline: '您处于离线状态',
    online: '您已恢复在线',
    syncPending: '更改将在您在线时同步',
    syncing: '同步中...',
    synced: '所有更改已同步',
  },
};

// ---------------------------------------------------------------------------
// Enrich all translation objects with missing / aliased keys so that
// components which reference top-level keys (navigation.*, app.*,
// organization.*, errors.generic, members.add, dues.collect, …) resolve to
// translated values instead of rendering raw key strings.
// ---------------------------------------------------------------------------
(function enrichTranslations() {
  const allLangs = [
    enTranslations, frTranslations, esTranslations,
    itTranslations, deTranslations, zhTranslations,
  ] as unknown as Record<string, Record<string, unknown>>[];

  allLangs.forEach((lang) => {
    const c = (lang.common ?? {}) as Record<string, unknown>;
    const langSettings = (lang.settings ?? {}) as Record<string, unknown>;
    const langFinances = (lang.finances ?? {}) as Record<string, unknown>;
    const langMembers = lang.members as Record<string, unknown> | undefined;
    const langDues = lang.dues as Record<string, unknown> | undefined;
    const langAttendance = lang.attendance as Record<string, unknown> | undefined;
    const langDonations = lang.donations as Record<string, unknown> | undefined;
    const langErrors = lang.errors as Record<string, unknown> | undefined;
    const baseOrg = (c.organization ?? {}) as Record<string, unknown>;

    // 1. Top-level navigation – components call t('navigation.X')
    if (!lang.navigation) {
      lang.navigation = c.navigation as Record<string, unknown>;
    }

    // 2. Top-level app – components call t('app.name'), t('app.cancel'), etc.
    if (!lang.app) {
      lang.app = {
        ...((c.app ?? {}) as Record<string, unknown>),
        loading: c.loading,
        save: c.save,
        cancel: c.cancel,
        create: c.create,
      };
    }

    // 3. Top-level organization with the full key set components need.
    //    createSuccess / updateSuccess / noOrganizations / createFirst are
    //    overlaid from JSON for en/fr/es below; it/de/zh fall back to 'en'.
    lang.organization = {
      title: baseOrg.title,
      select: baseOrg.select,
      create: baseOrg.create,
      name: langSettings.organizationName ?? '',
      currency: c.currency ?? '',
      language: c.language ?? '',
      monthlyDues: langSettings.monthlyDuesAmount ?? '',
      address: c.address ?? '',
      phone: c.phone ?? '',
      email: c.email ?? '',
    };

    // 4. errors.generic – inline objects only have errors.general
    if (langErrors && !langErrors.generic) {
      langErrors.generic = langErrors.general;
    }

    // 5. members – add key aliases used by components
    if (langMembers) {
      if (!langMembers.add) langMembers.add = langMembers.addMember;
      if (!langMembers.deleteConfirm) langMembers.deleteConfirm = langMembers.confirmDelete;
      if (!langMembers.createSuccess) langMembers.createSuccess = langMembers.saveSuccess;
      if (!langMembers.search) langMembers.search = langMembers.searchMembers;
    }

    // 6. dues – add key aliases used by components
    if (langDues) {
      if (!langDues.collect) langDues.collect = langDues.collectDues;
      if (!langDues.collection) langDues.collection = langDues.duesCollection;
      if (!langDues.transfer) langDues.transfer = langDues.bankTransfer;
      if (!langDues.monthlyAmount) langDues.monthlyAmount = langDues.monthlyDues;
      if (!langDues.current) langDues.current = langDues.currentMonth;
    }

    // 7. attendance – add key alias used by components
    if (langAttendance && !langAttendance.createMeeting) {
      langAttendance.createMeeting = langAttendance.addMeeting;
    }

    // 8. donations – add key alias used by components
    if (langDonations && !langDonations.createEvent) {
      langDonations.createEvent = langDonations.addEvent;
    }

    // 9. common.thisMonth – used in Home.tsx; value lives in finances.thisMonth
    if (!c.thisMonth && langFinances.thisMonth) {
      c.thisMonth = langFinances.thisMonth;
    }
  });

  // For en / fr / es: overlay the JSON locale files.
  // They contain the correct/canonical key names that components call plus
  // keys absent from the inline translation objects (e.g. dues.paymentSaved,
  // members.title_label, organization.createSuccess, errors.generic, …).
  const jsonPairs: [Record<string, Record<string, unknown>>, Record<string, unknown>][] = [
    [enTranslations as unknown as Record<string, Record<string, unknown>>, enLocale as unknown as Record<string, unknown>],
    [frTranslations as unknown as Record<string, Record<string, unknown>>, frLocale as unknown as Record<string, unknown>],
    [esTranslations as unknown as Record<string, Record<string, unknown>>, esLocale as unknown as Record<string, unknown>],
  ];

  const overlaySections = [
    'organization', 'members', 'attendance', 'dues',
    'donations', 'finances', 'memberPortal', 'errors', 'settings', 'auth',
  ];

  jsonPairs.forEach(([base, json]) => {
    overlaySections.forEach((section) => {
      if (json[section]) {
        base[section] = { ...(base[section] as object), ...(json[section] as object) };
      }
    });
    if (json.navigation) base.navigation = json.navigation as Record<string, unknown>;
    if (json.app) base.app = { ...(base.app as object), ...(json.app as object) };
  });
}());

// Public pages translations
const publicTranslations = {
  en: {
    home: {
      hero: {
        title: 'Manage Your Organization with Ease',
        subtitle: 'Tickally helps professional bodies, academic organisations, cultural associations, churches, alumni groups, and cooperatives track members, collect dues, and manage finances—all in one place.',
        cta: {
          orgs: 'For Organizations',
          members: 'For Members',
        },
      },
      orgs: {
        title: 'For Organizations',
        description: 'Admins, treasurers, and secretaries can manage members, track attendance, collect dues, and generate financial reports.',
        features: [
          'Member management & attendance tracking',
          'Dues collection with smart allocation',
          'Donation & event management',
          'Financial reports & summaries',
        ],
        cta: 'Register Your Organization',
      },
      members: {
        title: 'For Members',
        description: 'Members can view their attendance records, check dues status, and see their contribution history.',
        features: [
          'View personal attendance records',
          'Check dues & payment status',
          'See donation history',
          'Update profile information',
        ],
        cta: 'Join Your Organization',
      },
      tagline: 'Free for members, organisation pays',
    },
    howItWorks: {
      title: 'How Tickally Works',
      steps: [
        {
          title: 'Create Your Organization',
          description: 'Sign up and set up your organization profile with currency, language, and monthly dues amount.',
        },
        {
          title: 'Add Your Members',
          description: 'Import or manually add members. Each member gets a unique profile to track their activity.',
        },
        {
          title: 'Track Attendance',
          description: 'Use meeting mode to quickly mark attendance with a simple checkbox interface.',
        },
        {
          title: 'Collect Dues',
          description: 'Record payments and let Tickally automatically allocate to backlogs, current month, or prepay.',
        },
        {
          title: 'Generate Reports',
          description: 'View financial summaries, attendance statistics, and export reports for your records.',
        },
      ],
    },
    about: {
      title: 'About Tickally',
      mission: {
        title: 'Our Mission',
        description: 'Tickally was built to simplify organization management for informal groups worldwide. We believe every community deserves easy-to-use tools to manage their members and finances.',
      },
      values: [
        { title: 'Simplicity', description: 'Easy to use, no training required' },
        { title: 'Accessibility', description: 'Works on any device, anywhere' },
        { title: 'Affordability', description: 'Free for small organizations' },
        { title: 'Security', description: 'Your data is encrypted and protected' },
      ],
    },
    nav: {
      home: 'Home',
      howItWorks: 'How It Works',
      about: 'About',
      pricing: 'Pricing',
      login: 'Login',
      signup: 'Sign Up',
    },
    footer: {
      copyright: '© 2024 Tickally. All rights reserved.',
    },
    pricing: {
      title: 'Pricing Plans',
      subtitle: 'Choose the plan that fits your organization',
      cta: 'Get Started',
      contactUs: 'Contact Us',
      mostPopular: 'Most Popular',
      perMonth: '/month',
      plans: {
        free: {
          name: 'Free',
          price: '€0',
          description: 'Perfect for small groups getting started',
          features: [
            'Attendance taking & tracking',
            'Membership status reports',
            'Up to 10 members',
            'Basic support',
          ],
        },
        basic: {
          name: 'Basic',
          price: '€5',
          priceUsd: '~$5.50',
          description: 'For growing organizations',
          features: [
            'Everything in Free',
            'Monthly dues tracking',
            'Donations management',
            'All reports (attendance & financial)',
            'Up to 5 admin users',
            'Email support',
          ],
        },
        intermediate: {
          name: 'Intermediate',
          price: '€10',
          priceUsd: '~$11',
          description: 'With Member Portal access',
          features: [
            'Everything in Basic',
            'Member Portal with join code',
            'Members can view their records',
            'Up to 40 users total',
            'Priority support',
          ],
        },
        advanced: {
          name: 'Advanced',
          price: '€15',
          priceUsd: '~$16.50',
          description: 'For larger organizations',
          features: [
            'Everything in Intermediate',
            'Up to 100 users',
            'Advanced analytics',
            'Dedicated support',
            'Custom integrations',
          ],
        },
      },
      custom: {
        title: 'Need more?',
        description: 'For organizations with over 100 members, we offer custom plans tailored to your needs.',
        button: 'Contact for Custom Plan',
      },
      regionalNote: 'Regional pricing available in local currencies. Contact us for details.',
    },
  },
  fr: {
    home: {
      hero: {
        title: 'Gérez Votre Organisation Facilement',
        subtitle: 'Tickally aide les organismes professionnels, les organisations académiques, les associations culturelles, les églises, les groupes d\'anciens élèves et les coopératives à suivre les membres, collecter les cotisations et gérer les finances—tout en un seul endroit.',
        cta: {
          orgs: 'Pour les Organisations',
          members: 'Pour les Membres',
        },
      },
      orgs: {
        title: 'Pour les Organisations',
        description: 'Les administrateurs, trésoriers et secrétaires peuvent gérer les membres, suivre les présences, collecter les cotisations et générer des rapports financiers.',
        features: [
          'Gestion des membres et suivi des présences',
          'Collecte des cotisations avec allocation intelligente',
          'Gestion des dons et événements',
          'Rapports financiers et résumés',
        ],
        cta: 'Enregistrer Votre Organisation',
      },
      members: {
        title: 'Pour les Membres',
        description: 'Les membres peuvent consulter leurs registres de présence, vérifier le statut des cotisations et voir l\'historique de leurs contributions.',
        features: [
          'Consulter les registres de présence personnels',
          'Vérifier les cotisations et le statut de paiement',
          'Voir l\'historique des dons',
          'Mettre à jour les informations de profil',
        ],
        cta: 'Rejoindre Votre Organisation',
      },
      tagline: 'Gratuit pour les membres, l\'organisation paie',
    },
    howItWorks: {
      title: 'Comment Fonctionne Tickally',
      steps: [
        {
          title: 'Créez Votre Organisation',
          description: 'Inscrivez-vous et configurez le profil de votre organisation avec la devise, la langue et le montant des cotisations mensuelles.',
        },
        {
          title: 'Ajoutez Vos Membres',
          description: 'Importez ou ajoutez manuellement des membres. Chaque membre reçoit un profil unique pour suivre son activité.',
        },
        {
          title: 'Suivez les Présences',
          description: 'Utilisez le mode réunion pour marquer rapidement les présences avec une interface simple de cases à cocher.',
        },
        {
          title: 'Collectez les Cotisations',
          description: 'Enregistrez les paiements et laissez Tickally allouer automatiquement aux arriérés, au mois en cours ou au prépaiement.',
        },
        {
          title: 'Générez des Rapports',
          description: 'Consultez les résumés financiers, les statistiques de présence et exportez des rapports pour vos archives.',
        },
      ],
    },
    about: {
      title: 'À Propos de Tickally',
      mission: {
        title: 'Notre Mission',
        description: 'Tickally a été créé pour simplifier la gestion des organisations pour les groupes informels du monde entier. Nous croyons que chaque communauté mérite des outils faciles à utiliser pour gérer ses membres et ses finances.',
      },
      values: [
        { title: 'Simplicité', description: 'Facile à utiliser, aucune formation requise' },
        { title: 'Accessibilité', description: 'Fonctionne sur n\'importe quel appareil, n\'importe où' },
        { title: 'Abordabilité', description: 'Gratuit pour les petites organisations' },
        { title: 'Sécurité', description: 'Vos données sont chiffrées et protégées' },
      ],
    },
    nav: {
      home: 'Accueil',
      howItWorks: 'Comment Ça Marche',
      about: 'À Propos',
      pricing: 'Tarifs',
      login: 'Connexion',
      signup: 'S\'inscrire',
    },
    footer: {
      copyright: '© 2024 Tickally. Tous droits réservés.',
    },
    pricing: {
      title: 'Forfaits',
      subtitle: 'Choisissez le forfait qui convient à votre organisation',
      cta: 'Commencer',
      contactUs: 'Contactez-nous',
      mostPopular: 'Le Plus Populaire',
      perMonth: '/mois',
      plans: {
        free: {
          name: 'Gratuit',
          price: '€0',
          description: 'Parfait pour les petits groupes',
          features: [
            'Prise de présence & suivi',
            'Rapports de statut des membres',
            'Jusqu\'à 10 membres',
            'Support de base',
          ],
        },
        basic: {
          name: 'Basique',
          price: '€5',
          priceUsd: '~$5.50',
          description: 'Pour les organisations en croissance',
          features: [
            'Tout dans Gratuit',
            'Suivi des cotisations mensuelles',
            'Gestion des dons',
            'Tous les rapports',
            'Jusqu\'à 5 utilisateurs admin',
            'Support par email',
          ],
        },
        intermediate: {
          name: 'Intermédiaire',
          price: '€10',
          priceUsd: '~$11',
          description: 'Avec accès Espace Membre',
          features: [
            'Tout dans Basique',
            'Espace Membre avec code',
            'Les membres voient leurs dossiers',
            'Jusqu\'à 40 utilisateurs',
            'Support prioritaire',
          ],
        },
        advanced: {
          name: 'Avancé',
          price: '€15',
          priceUsd: '~$16.50',
          description: 'Pour les grandes organisations',
          features: [
            'Tout dans Intermédiaire',
            'Jusqu\'à 100 utilisateurs',
            'Analyses avancées',
            'Support dédié',
            'Intégrations personnalisées',
          ],
        },
      },
      custom: {
        title: 'Besoin de plus?',
        description: 'Pour les organisations de plus de 100 membres, nous proposons des forfaits sur mesure.',
        button: 'Contactez pour un Forfait Personnalisé',
      },
      regionalNote: 'Tarifs régionaux disponibles dans les monnaies locales. Contactez-nous pour plus de détails.',
    },
  },
  es: {
    home: {
      hero: {
        title: 'Gestione Su Organización con Facilidad',
        subtitle: 'Tickally ayuda a organismos profesionales, organizaciones académicas, asociaciones culturales, iglesias, grupos de exalumnos y cooperativas a rastrear miembros, recolectar cuotas y administrar finanzas—todo en un solo lugar.',
        cta: {
          orgs: 'Para Organizaciones',
          members: 'Para Miembros',
        },
      },
      orgs: {
        title: 'Para Organizaciones',
        description: 'Los administradores, tesoreros y secretarios pueden gestionar miembros, rastrear asistencia, recolectar cuotas y generar informes financieros.',
        features: [
          'Gestión de miembros y seguimiento de asistencia',
          'Recaudación de cuotas con asignación inteligente',
          'Gestión de donaciones y eventos',
          'Informes financieros y resúmenes',
        ],
        cta: 'Registrar Su Organización',
      },
      members: {
        title: 'Para Miembros',
        description: 'Los miembros pueden ver sus registros de asistencia, verificar el estado de las cuotas y ver su historial de contribuciones.',
        features: [
          'Ver registros personales de asistencia',
          'Verificar cuotas y estado de pago',
          'Ver historial de donaciones',
          'Actualizar información de perfil',
        ],
        cta: 'Unirse a Su Organización',
      },
      tagline: 'Gratis para miembros, la organización paga',
    },
    howItWorks: {
      title: 'Cómo Funciona Tickally',
      steps: [
        {
          title: 'Cree Su Organización',
          description: 'Regístrese y configure el perfil de su organización con moneda, idioma y monto de cuotas mensuales.',
        },
        {
          title: 'Agregue Sus Miembros',
          description: 'Importe o agregue manualmente miembros. Cada miembro recibe un perfil único para rastrear su actividad.',
        },
        {
          title: 'Rastree la Asistencia',
          description: 'Use el modo de reunión para marcar rápidamente la asistencia con una interfaz simple de casillas.',
        },
        {
          title: 'Recolecte Cuotas',
          description: 'Registre pagos y deje que Tickally asigne automáticamente a atrasos, mes actual o prepago.',
        },
        {
          title: 'Genere Informes',
          description: 'Vea resúmenes financieros, estadísticas de asistencia y exporte informes para sus registros.',
        },
      ],
    },
    about: {
      title: 'Acerca de Tickally',
      mission: {
        title: 'Nuestra Misión',
        description: 'Tickally fue creado para simplificar la gestión de organizaciones para grupos informales en todo el mundo. Creemos que cada comunidad merece herramientas fáciles de usar para gestionar sus miembros y finanzas.',
      },
      values: [
        { title: 'Simplicidad', description: 'Fácil de usar, no se requiere capacitación' },
        { title: 'Accesibilidad', description: 'Funciona en cualquier dispositivo, en cualquier lugar' },
        { title: 'Asequibilidad', description: 'Gratuito para organizaciones pequeñas' },
        { title: 'Seguridad', description: 'Sus datos están encriptados y protegidos' },
      ],
    },
    nav: {
      home: 'Inicio',
      howItWorks: 'Cómo Funciona',
      about: 'Acerca de',
      pricing: 'Precios',
      login: 'Iniciar Sesión',
      signup: 'Registrarse',
    },
    footer: {
      copyright: '© 2024 Tickally. Todos los derechos reservados.',
    },
    pricing: {
      title: 'Planes de Precios',
      subtitle: 'Elige el plan que se adapte a tu organización',
      cta: 'Comenzar',
      contactUs: 'Contáctanos',
      mostPopular: 'Más Popular',
      perMonth: '/mes',
      plans: {
        free: {
          name: 'Gratis',
          price: '€0',
          description: 'Perfecto para grupos pequeños',
          features: [
            'Toma y seguimiento de asistencia',
            'Informes de estado de membresía',
            'Hasta 10 miembros',
            'Soporte básico',
          ],
        },
        basic: {
          name: 'Básico',
          price: '€5',
          priceUsd: '~$5.50',
          description: 'Para organizaciones en crecimiento',
          features: [
            'Todo en Gratis',
            'Seguimiento de cuotas mensuales',
            'Gestión de donaciones',
            'Todos los informes',
            'Hasta 5 usuarios admin',
            'Soporte por email',
          ],
        },
        intermediate: {
          name: 'Intermedio',
          price: '€10',
          priceUsd: '~$11',
          description: 'Con acceso al Área de Miembros',
          features: [
            'Todo en Básico',
            'Área de Miembros con código',
            'Los miembros ven sus registros',
            'Hasta 40 usuarios',
            'Soporte prioritario',
          ],
        },
        advanced: {
          name: 'Avanzado',
          price: '€15',
          priceUsd: '~$16.50',
          description: 'Para organizaciones grandes',
          features: [
            'Todo en Intermedio',
            'Hasta 100 usuarios',
            'Análisis avanzados',
            'Soporte dedicado',
            'Integraciones personalizadas',
          ],
        },
      },
      custom: {
        title: '¿Necesitas más?',
        description: 'Para organizaciones con más de 100 miembros, ofrecemos planes personalizados.',
        button: 'Contactar para Plan Personalizado',
      },
      regionalNote: 'Precios regionales disponibles en monedas locales. Contáctanos para más detalles.',
    },
  },
  it: {
    home: {
      hero: {
        title: 'Gestisci la Tua Organizzazione con Facilità',
        subtitle: 'Tickally aiuta enti professionali, organizzazioni accademiche, associazioni culturali, chiese, gruppi di ex studenti e cooperative a tenere traccia dei membri, riscuotere quote e gestire le finanze—tutto in un unico posto.',
        cta: {
          orgs: 'Per le Organizzazioni',
          members: 'Per i Membri',
        },
      },
      orgs: {
        title: 'Per le Organizzazioni',
        description: 'Amministratori, tesorieri e segretari possono gestire i membri, tenere traccia delle presenze, riscuotere quote e generare rapporti finanziari.',
        features: [
          'Gestione membri e tracciamento presenze',
          'Riscossione quote con allocazione intelligente',
          'Gestione donazioni ed eventi',
          'Rapporti finanziari e riepiloghi',
        ],
        cta: 'Registra la Tua Organizzazione',
      },
      members: {
        title: 'Per i Membri',
        description: 'I membri possono visualizzare i loro registri di presenza, verificare lo stato delle quote e vedere la cronologia dei loro contributi.',
        features: [
          'Visualizza registri personali di presenza',
          'Verifica quote e stato dei pagamenti',
          'Vedi cronologia delle donazioni',
          'Aggiorna informazioni del profilo',
        ],
        cta: 'Unisciti alla Tua Organizzazione',
      },
      tagline: 'Gratuito per i membri, paga l\'organizzazione',
    },
    howItWorks: {
      title: 'Come Funziona Tickally',
      steps: [
        {
          title: 'Crea la Tua Organizzazione',
          description: 'Registrati e configura il profilo della tua organizzazione con valuta, lingua e importo delle quote mensili.',
        },
        {
          title: 'Aggiungi i Tuoi Membri',
          description: 'Importa o aggiungi manualmente i membri. Ogni membro riceve un profilo unico per tracciare la sua attività.',
        },
        {
          title: 'Traccia le Presenze',
          description: 'Usa la modalità riunione per segnare rapidamente le presenze con un\'interfaccia semplice di caselle di controllo.',
        },
        {
          title: 'Riscuoti le Quote',
          description: 'Registra i pagamenti e lascia che Tickally allochi automaticamente agli arretrati, al mese corrente o al prepagato.',
        },
        {
          title: 'Genera Rapporti',
          description: 'Visualizza riepiloghi finanziari, statistiche di presenza ed esporta rapporti per i tuoi archivi.',
        },
      ],
    },
    about: {
      title: 'Informazioni su Tickally',
      mission: {
        title: 'La Nostra Missione',
        description: 'Tickally è stato creato per semplificare la gestione delle organizzazioni per i gruppi informali in tutto il mondo. Crediamo che ogni comunità meriti strumenti facili da usare per gestire i propri membri e finanze.',
      },
      values: [
        { title: 'Semplicità', description: 'Facile da usare, nessuna formazione richiesta' },
        { title: 'Accessibilità', description: 'Funziona su qualsiasi dispositivo, ovunque' },
        { title: 'Affidabilità', description: 'Gratuito per le piccole organizzazioni' },
        { title: 'Sicurezza', description: 'I tuoi dati sono crittografati e protetti' },
      ],
    },
    nav: {
      home: 'Home',
      howItWorks: 'Come Funziona',
      about: 'Chi Siamo',
      pricing: 'Prezzi',
      login: 'Accedi',
      signup: 'Iscriviti',
    },
    footer: {
      copyright: '© 2024 Tickally. Tutti i diritti riservati.',
    },
    pricing: {
      title: 'Piani Tariffari',
      subtitle: 'Scegli il piano adatto alla tua organizzazione',
      cta: 'Inizia',
      contactUs: 'Contattaci',
      mostPopular: 'Più Popolare',
      perMonth: '/mese',
      plans: {
        free: {
          name: 'Gratuito',
          price: '€0',
          description: 'Perfetto per piccoli gruppi',
          features: [
            'Presenze e tracciamento',
            'Rapporti stato membri',
            'Fino a 10 membri',
            'Supporto base',
          ],
        },
        basic: {
          name: 'Base',
          price: '€5',
          priceUsd: '~$5.50',
          description: 'Per organizzazioni in crescita',
          features: [
            'Tutto in Gratuito',
            'Tracciamento quote mensili',
            'Gestione donazioni',
            'Tutti i rapporti',
            'Fino a 5 utenti admin',
            'Supporto email',
          ],
        },
        intermediate: {
          name: 'Intermedio',
          price: '€10',
          priceUsd: '~$11',
          description: 'Con accesso Area Membri',
          features: [
            'Tutto in Base',
            'Area Membri con codice',
            'I membri vedono i loro dati',
            'Fino a 40 utenti',
            'Supporto prioritario',
          ],
        },
        advanced: {
          name: 'Avanzato',
          price: '€15',
          priceUsd: '~$16.50',
          description: 'Per grandi organizzazioni',
          features: [
            'Tutto in Intermedio',
            'Fino a 100 utenti',
            'Analisi avanzate',
            'Supporto dedicato',
            'Integrazioni personalizzate',
          ],
        },
      },
      custom: {
        title: 'Serve di più?',
        description: 'Per organizzazioni con oltre 100 membri, offriamo piani personalizzati.',
        button: 'Contatta per Piano Personalizzato',
      },
      regionalNote: 'Prezzi regionali disponibili in valute locali. Contattaci per dettagli.',
    },
  },
  de: {
    home: {
      hero: {
        title: 'Verwalten Sie Ihre Organisation mit Leichtigkeit',
        subtitle: 'Tickally hilft Berufsverbänden, akademischen Organisationen, kulturellen Vereinen, Kirchen, Alumnigruppen und Genossenschaften dabei, Mitglieder zu verfolgen, Beiträge einzuziehen und Finanzen zu verwalten—alles an einem Ort.',
        cta: {
          orgs: 'Für Organisationen',
          members: 'Für Mitglieder',
        },
      },
      orgs: {
        title: 'Für Organisationen',
        description: 'Administratoren, Schatzmeister und Sekretäre können Mitglieder verwalten, Anwesenheit verfolgen, Beiträge einsammeln und Finanzberichte erstellen.',
        features: [
          'Mitgliederverwaltung & Anwesenheitsverfolgung',
          'Beitragseinzug mit intelligenter Zuweisung',
          'Spenden- & Veranstaltungsmanagement',
          'Finanzberichte & Zusammenfassungen',
        ],
        cta: 'Ihre Organisation Registrieren',
      },
      members: {
        title: 'Für Mitglieder',
        description: 'Mitglieder können ihre Anwesenheitsaufzeichnungen einsehen, den Beitragsstatus überprüfen und ihren Beitragsverlauf anzeigen.',
        features: [
          'Persönliche Anwesenheitsaufzeichnungen anzeigen',
          'Beiträge & Zahlungsstatus überprüfen',
          'Spendenverlauf anzeigen',
          'Profilinformationen aktualisieren',
        ],
        cta: 'Ihrer Organisation Beitreten',
      },
      tagline: 'Kostenlos für Mitglieder, die Organisation zahlt',
    },
    howItWorks: {
      title: 'Wie Tickally Funktioniert',
      steps: [
        {
          title: 'Erstellen Sie Ihre Organisation',
          description: 'Melden Sie sich an und richten Sie Ihr Organisationsprofil mit Währung, Sprache und monatlichem Beitragsbetrag ein.',
        },
        {
          title: 'Fügen Sie Ihre Mitglieder Hinzu',
          description: 'Importieren oder fügen Sie manuell Mitglieder hinzu. Jedes Mitglied erhält ein einzigartiges Profil zur Verfolgung seiner Aktivitäten.',
        },
        {
          title: 'Verfolgen Sie die Anwesenheit',
          description: 'Verwenden Sie den Besprechungsmodus, um Anwesenheiten schnell mit einer einfachen Kontrollkästchen-Oberfläche zu markieren.',
        },
        {
          title: 'Beiträge Einsammeln',
          description: 'Erfassen Sie Zahlungen und lassen Sie Tickally automatisch auf Rückstände, aktuellen Monat oder Vorauszahlung zuweisen.',
        },
        {
          title: 'Berichte Erstellen',
          description: 'Sehen Sie sich Finanzzusammenfassungen, Anwesenheitsstatistiken an und exportieren Sie Berichte für Ihre Aufzeichnungen.',
        },
      ],
    },
    about: {
      title: 'Über Tickally',
      mission: {
        title: 'Unsere Mission',
        description: 'Tickally wurde entwickelt, um die Organisationsverwaltung für informelle Gruppen weltweit zu vereinfachen. Wir glauben, dass jede Community einfach zu bedienende Tools verdient, um ihre Mitglieder und Finanzen zu verwalten.',
      },
      values: [
        { title: 'Einfachheit', description: 'Einfach zu bedienen, keine Schulung erforderlich' },
        { title: 'Zugänglichkeit', description: 'Funktioniert auf jedem Gerät, überall' },
        { title: 'Erschwinglichkeit', description: 'Kostenlos für kleine Organisationen' },
        { title: 'Sicherheit', description: 'Ihre Daten sind verschlüsselt und geschützt' },
      ],
    },
    nav: {
      home: 'Startseite',
      howItWorks: 'Wie Es Funktioniert',
      about: 'Über Uns',
      pricing: 'Preise',
      login: 'Anmelden',
      signup: 'Registrieren',
    },
    footer: {
      copyright: '© 2024 Tickally. Alle Rechte vorbehalten.',
    },
    pricing: {
      title: 'Preispläne',
      subtitle: 'Wählen Sie den Plan, der zu Ihrer Organisation passt',
      cta: 'Loslegen',
      contactUs: 'Kontaktieren Sie uns',
      mostPopular: 'Beliebteste',
      perMonth: '/Monat',
      plans: {
        free: {
          name: 'Kostenlos',
          price: '€0',
          description: 'Perfekt für kleine Gruppen',
          features: [
            'Anwesenheitserfassung & -verfolgung',
            'Mitgliedschaftsstatus-Berichte',
            'Bis zu 10 Mitglieder',
            'Basis-Support',
          ],
        },
        basic: {
          name: 'Basis',
          price: '€5',
          priceUsd: '~$5.50',
          description: 'Für wachsende Organisationen',
          features: [
            'Alles in Kostenlos',
            'Monatliche Beitragsverfolgung',
            'Spendenverwaltung',
            'Alle Berichte',
            'Bis zu 5 Admin-Benutzer',
            'E-Mail-Support',
          ],
        },
        intermediate: {
          name: 'Mittel',
          price: '€10',
          priceUsd: '~$11',
          description: 'Mit Mitgliederbereich-Zugang',
          features: [
            'Alles in Basis',
            'Mitgliederbereich mit Code',
            'Mitglieder sehen ihre Daten',
            'Bis zu 40 Benutzer',
            'Prioritäts-Support',
          ],
        },
        advanced: {
          name: 'Erweitert',
          price: '€15',
          priceUsd: '~$16.50',
          description: 'Für größere Organisationen',
          features: [
            'Alles in Mittel',
            'Bis zu 100 Benutzer',
            'Erweiterte Analysen',
            'Dedizierter Support',
            'Benutzerdefinierte Integrationen',
          ],
        },
      },
      custom: {
        title: 'Mehr benötigt?',
        description: 'Für Organisationen mit über 100 Mitgliedern bieten wir maßgeschneiderte Pläne.',
        button: 'Kontakt für Maßgeschneiderten Plan',
      },
      regionalNote: 'Regionale Preise in lokalen Währungen verfügbar. Kontaktieren Sie uns für Details.',
    },
  },
  zh: {
    home: {
      hero: {
        title: '轻松管理您的组织',
        subtitle: 'Tickally帮助专业团体、学术组织、文化协会、教会、校友团体和合作社追踪成员、收取会费和管理财务—所有这些都在一个地方完成。',
        cta: {
          orgs: '组织入口',
          members: '成员入口',
        },
      },
      orgs: {
        title: '面向组织',
        description: '管理员、财务主管和秘书可以管理成员、追踪出勤、收取会费并生成财务报告。',
        features: [
          '成员管理和出勤追踪',
          '智能分配的会费收取',
          '捐赠和活动管理',
          '财务报告和摘要',
        ],
        cta: '注册您的组织',
      },
      members: {
        title: '面向成员',
        description: '成员可以查看他们的出勤记录、检查会费状态并查看他们的贡献历史。',
        features: [
          '查看个人出勤记录',
          '检查会费和付款状态',
          '查看捐赠历史',
          '更新个人资料信息',
        ],
        cta: '加入您的组织',
      },
      tagline: '会员免费，由组织付费',
    },
    howItWorks: {
      title: 'Tickally如何运作',
      steps: [
        {
          title: '创建您的组织',
          description: '注册并设置您的组织资料，包括货币、语言和月度会费金额。',
        },
        {
          title: '添加您的成员',
          description: '导入或手动添加成员。每个成员都会获得一个独特的个人资料来追踪他们的活动。',
        },
        {
          title: '追踪出勤',
          description: '使用会议模式，通过简单的复选框界面快速标记出勤。',
        },
        {
          title: '收取会费',
          description: '记录付款，让Tickally自动分配到欠款、当月或预付款。',
        },
        {
          title: '生成报告',
          description: '查看财务摘要、出勤统计并导出报告以供记录。',
        },
      ],
    },
    about: {
      title: '关于Tickally',
      mission: {
        title: '我们的使命',
        description: 'Tickally的建立是为了简化全球非正式组织的管理工作。我们相信每个社区都应该拥有易于使用的工具来管理其成员和财务。',
      },
      values: [
        { title: '简洁性', description: '易于使用，无需培训' },
        { title: '可访问性', description: '可在任何设备、任何地方使用' },
        { title: '经济性', description: '小型组织免费使用' },
        { title: '安全性', description: '您的数据已加密并受到保护' },
      ],
    },
    nav: {
      home: '首页',
      howItWorks: '如何运作',
      about: '关于我们',
      pricing: '价格',
      login: '登录',
      signup: '注册',
    },
    footer: {
      copyright: '© 2024 Tickally。保留所有权利。',
    },
    pricing: {
      title: '价格方案',
      subtitle: '选择适合您组织的方案',
      cta: '开始使用',
      contactUs: '联系我们',
      mostPopular: '最受欢迎',
      perMonth: '/月',
      plans: {
        free: {
          name: '免费版',
          price: '€0',
          description: '适合小型团体入门使用',
          features: [
            '出勤记录与追踪',
            '会员状态报告',
            '最多10名成员',
            '基础支持',
          ],
        },
        basic: {
          name: '基础版',
          price: '€5',
          priceUsd: '~$5.50',
          description: '适合成长中的组织',
          features: [
            '免费版全部功能',
            '月度会费追踪',
            '捐赠管理',
            '所有报告',
            '最多5个管理员账号',
            '邮件支持',
          ],
        },
        intermediate: {
          name: '中级版',
          price: '€10',
          priceUsd: '~$11',
          description: '包含会员门户访问',
          features: [
            '基础版全部功能',
            '会员门户与加入码',
            '会员可查看个人记录',
            '最多40个用户',
            '优先支持',
          ],
        },
        advanced: {
          name: '高级版',
          price: '€15',
          priceUsd: '~$16.50',
          description: '适合大型组织',
          features: [
            '中级版全部功能',
            '最多100个用户',
            '高级分析',
            '专属支持',
            '自定义集成',
          ],
        },
      },
      custom: {
        title: '需要更多？',
        description: '对于超过100名成员的组织，我们提供定制化方案。',
        button: '联系获取定制方案',
      },
      regionalNote: '提供本地货币的区域价格。联系我们了解详情。',
    },
  },
};

// Merge public translations into each language
Object.keys(publicTranslations).forEach((lang) => {
  const translations = {
    en: enTranslations,
    fr: frTranslations,
    es: esTranslations,
    it: itTranslations,
    de: deTranslations,
    zh: zhTranslations,
  }[lang];
  if (translations) {
    Object.assign(translations, { public: publicTranslations[lang as keyof typeof publicTranslations] });
  }
});

// Get saved language from localStorage or default to 'en'
const getSavedLanguage = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('tickally-language') || 'en';
  }
  return 'en';
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      fr: { translation: frTranslations },
      es: { translation: esTranslations },
      it: { translation: itTranslations },
      de: { translation: deTranslations },
      zh: { translation: zhTranslations },
    },
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'es', 'it', 'de', 'zh'],
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// Custom language change handler that also saves to localStorage
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
i18n.changeLanguage = async (lng: string | undefined) => {
  const result = await originalChangeLanguage(lng);
  if (lng && typeof window !== 'undefined') {
    localStorage.setItem('tickally-language', lng);
  }
  return result;
};

export default i18n;
